import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormLabel,
    IconButton,
    Menu,
    MenuItem,
    Radio,
    RadioGroup,
    Step,
    StepButton,
    Stepper,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router';
import { Prompt, Redirect, useLocation, useParams } from 'react-router-dom';

import { DataAPIContext } from '../api';
import AttachmentImagePicker from '../components/AttachmentImagePicker';
import FormActionButtons from '../components/FormActionButtons';
import PageMeta from '../components/PageMeta';
import QuillEditor from '../components/QuillEditor';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import CampaignAudienceBuilder, { isFilterRowIncomplete } from './CampaignAudienceBuilder';
import CampaignDeliveryMethodPreviewDialog from './CampaignDeliveryMethodPreviewDialog';
import CampaignReport from './CampaignReport';
import InlineImagePickerDialog from './InlineImagePickerDialog';
import MobileLengthNotice from './MobileLengthNotice';
import SmsSegmentCounter from './SmsSegmentCounter';
import { campaignMobileLength, MOBILE_MAX_CHARACTERS } from './smsSegments';
import {
    CAMPAIGN_PURPOSE,
    CAMPAIGN_READ_ONLY_STATUSES,
    CAMPAIGN_STATUS,
    CAMPAIGN_TIMING,
    DELIVERY_METHOD,
    SEND_AS,
    audienceModeForSendAs,
    appealCampaignPromo,
    checkCampaignPromo,
    createBlankCampaign,
    duplicateCampaignApi,
    fetchCampaignAudienceLanguages,
    fetchCampaignCapUsage,
    fetchCampaignCostEstimate,
    fetchCampaignFeatures,
    fetchCampaignWhatsAppUnreachable,
    fetchCampaignFilterOptions,
    fetchCampaignOne,
    operatorLocalTimeLabel,
    overrideCampaignPromo,
    previewCampaignTranslation,
    saveCampaign,
    sendCampaignApi,
    sendCampaignTestApi,
    userCanAccessCampaignsUi,
} from './campaignStorage';

const CAMPAIGNS_LIST_URL = '/notifications/campaigns';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        // No bottom padding: every step ends with the sticky stepActions bar, which should sit flush.
        height: `calc(${theme.cp.contentHeight} - 0.25rem)`,
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 3.5rem)`,
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        flexWrap: 'wrap',
    },
    title: {
        fontSize: 20,
        fontWeight: 600,
        flex: 1,
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
    },
    stepperWrap: {
        padding: theme.spacing(1.5, 2, 0.5),
        maxWidth: 1120,
    },
    // Single-column card stack with a comfortable reading width — wide
    // enough to use the screen without stretching form fields so far that
    // they're hard to scan.
    stepBody: {
        maxWidth: 1120,
        margin: '0 auto',
    },
    cardTitle: {
        fontWeight: 700,
        fontSize: 16,
        marginBottom: theme.spacing(2),
    },
    // Selectable option card used for Purpose (Promotional / Essential)
    // and the channel picker. Mirrors the bordered, click-to-select tiles
    // in the mockup; the selected state gets a blue ring + tint.
    optionCard: {
        border: '1.5px solid #E0E0E0',
        borderRadius: 10,
        padding: theme.spacing(1.5, 2),
        cursor: 'pointer',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        // Equal heights come from the CSS-grid track's default
        // ``align-items: stretch``; an explicit ``height: 100%`` here
        // rendered a hair taller than the grid cell and the bottom border
        // got clipped by the parent Card's ``overflow: hidden``.
        boxSizing: 'border-box',
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    optionCardSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#F3F8FF',
    },
    optionCardDisabled: {
        cursor: 'default',
        opacity: 0.6,
        '&:hover': { borderColor: '#E0E0E0' },
    },
    // CP-8423 (Martina): each step's bar sticks to the bottom of the screen while the step
    // scrolls, the same grey sticky bar as Edit Trip and Edit Waiver. It must stay the last
    // child of stepBody (the bar can't stick past its parent); the negative margins span
    // stepBody's px: 2 so the grey runs the full card width.
    stepActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(-2),
        marginRight: theme.spacing(-2),
        padding: theme.spacing(2),
        position: 'sticky',
        bottom: 0,
        zIndex: 1000,
        backgroundColor: '#F5F5F5',
    },
}));

const STATUS_CHIP = {
    [CAMPAIGN_STATUS.DRAFT]: { label: 'Draft', color: 'default' },
    [CAMPAIGN_STATUS.SCHEDULED]: { label: 'Scheduled', color: 'info' },
    [CAMPAIGN_STATUS.ONGOING]: { label: 'Ongoing', color: 'warning' },
    [CAMPAIGN_STATUS.SENT]: { label: 'Sent', color: 'success' },
    [CAMPAIGN_STATUS.FAILED]: { label: 'Failed', color: 'error' },
};

// Initial-launch toggle: hide Announcement send_as from the dashboard while
// leaving the backend pipeline + serializer in place. Flip to true to expose
// it once we're ready to ship that mode. Existing campaigns with
// send_as=announcement still load (the radio re-appears for those records so
// the operator can read/edit, but new campaigns can't pick it).
const SHOW_ANNOUNCEMENT_SEND_AS = false;

// Initial-launch toggle: hide the "Advanced" per-recipient ruleset picker
// from the Audience step — operators were confused about what it does. The
// backend ruleset plumbing stays intact; flip to true to re-expose it.
// Existing campaigns that already have a ruleset set still show the section
// (auto-expanded) so an operator can read or clear it.
const SHOW_ADVANCED_RULESET = false;

// Wizard steps: Compose → Audience → Send (→ Campaign Analytics). Compose
// holds the campaign basics (name / purpose), the channel picker, and the
// message body — purpose + channel choices drive the audience preview's
// reachability filters, so co-locating them keeps cause-and-effect in one
// place. Audience is its own surface (filter builder). Send is the review +
// send-now/schedule surface; once a campaign has been sent it becomes a
// read-only recap. Campaign Analytics is a fourth step that only appears
// after send (SENT/ONGOING) and hosts the results report — it's also
// deep-linkable from the list's "Campaign Analytics" action (?step=analytics).
// Mobile-channel media constraints. Mirrors the backend's per-channel
// gating (``_split_attachments_for_mobile`` in apiv2/lib/campaign_send.py)
// and Twilio's transport caps: media past the MMS 5 MB total budget, a
// WhatsApp file over 16 MB, or a WhatsApp image over 5 MB is delivered as
// a link instead of native media, so the builder warns while the operator
// can still fix it. Attachments with an unknown size (rows predating size
// capture) are given the benefit of the doubt.
const MMS_MEDIA_EXTENSIONS = ['jpg', 'jpeg', 'jfif', 'png', 'gif'];
const WHATSAPP_MEDIA_EXTENSIONS = [
    'jpg', 'jpeg', 'jfif', 'png', 'mp4',
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'jfif', 'png', 'gif'];
const MMS_TOTAL_MEDIA_BYTES = 5 * 1024 * 1024;
const MMS_MAX_MEDIA = 10;
const WHATSAPP_MEDIA_BYTES = 16 * 1024 * 1024;
const WHATSAPP_IMAGE_BYTES = 5 * 1024 * 1024;

const attachmentExtension = (att) => {
    const path = (att?.url || att?.name || '').split('?')[0].split('#')[0];
    const base = path.split('/').pop() || '';
    const dot = base.lastIndexOf('.');
    return dot >= 0 ? base.slice(dot + 1).toLowerCase() : '';
};

// How many attachments each mobile channel appends to the text as a link, as the backend splits them
// (``_split_attachments_for_mobile``): a type the channel can't carry, a WhatsApp file over its cap, and MMS media
// past 10 files or 5 MB in all. Feeds the 1,600-character count (``campaignMobileLength``).
export const mobileAttachmentLinks = (attachments) => {
    let smsLinks = 0;
    let whatsappLinks = 0;
    let mmsFiles = 0;
    let mmsBytes = 0;
    (attachments || []).forEach((a) => {
        const ext = attachmentExtension(a);
        const size = a.size || 0;
        if (!MMS_MEDIA_EXTENSIONS.includes(ext) || mmsFiles >= MMS_MAX_MEDIA
            || mmsBytes + size > MMS_TOTAL_MEDIA_BYTES) {
            smsLinks += 1;
        } else {
            mmsFiles += 1;
            mmsBytes += size;
        }
        const cap = IMAGE_EXTENSIONS.includes(ext) ? WHATSAPP_IMAGE_BYTES : WHATSAPP_MEDIA_BYTES;
        if (!WHATSAPP_MEDIA_EXTENSIONS.includes(ext) || size > cap) whatsappLinks += 1;
    });
    return { smsLinks, whatsappLinks };
};

const formatMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const STEP_COMPOSE = 'compose';
const STEP_AUDIENCE = 'audience';
const STEP_SEND = 'send';
const STEP_ANALYTICS = 'analytics';
const STEPS = [
    { key: STEP_COMPOSE, label: 'Compose' },
    { key: STEP_AUDIENCE, label: 'Audience' },
    { key: STEP_SEND, label: 'Send' },
];
// Analytics is only meaningful once a campaign has been sent (or is
// mid-send), so it's appended to the stepper conditionally — see
// ``visibleSteps`` in the component. Statuses that expose it are the same
// ones that render the results report (SENT/ONGOING).
const ANALYTICS_STEP = { key: STEP_ANALYTICS, label: 'Campaign Analytics' };
const ANALYTICS_STATUSES = [CAMPAIGN_STATUS.SENT, CAMPAIGN_STATUS.ONGOING];

/**
 * Curated set of template variables operators can insert into the rich
 * email body via QuillEditor's "Insert variable" menu.
 *
 * Tree shape note: QuillEditor builds the inserted token from the menu
 * path (the keys) — see `insertAtCursor` in `components/QuillEditor.js` —
 * so by default the keys must match the actual Django template variable
 * paths (`user > first_name` becomes `{{ user.first_name }}`). A leaf
 * whose value is a *string* overrides that: the string is used verbatim
 * as the template path, letting a menu entry live under one group while
 * inserting a token from another scope (e.g. Group tour > date inserts
 * `{{ participant.start_datetime.date_us }}`). ``true`` remains the
 * convention for "plain leaf, token comes from the key path."
 *
 * Mirrors what `_build_recipient_context` puts in scope on the backend
 * (apiv2/lib/campaign_send.py). Kept tight intentionally — operator
 * asks should grow the list, not a dump of every model attribute.
 *
 * ``date``/``time`` ride the participant context (tour-timezone,
 * pre-formatted, and per-recipient: bespoke tours use the tour's
 * start_datetime, date-tours use the guest's booked departure). Like
 * the other tour-scoped tokens they render empty in announcement mode
 * (no per-recipient gtu).
 *
 * ``voucher_link`` is the booking's voucher URL (short link when one
 * exists, otherwise the public media URL — see ``voucher`` in
 * get_tour_participant_event_context, demo/lib/events/types/utils.py).
 * Booking-scoped, so it renders blank in announcement mode and follows
 * the most-recent booking in Main Office mode; bookings without a
 * voucher file render blank. Listed under Guest because operators
 * think of it as "the guest's voucher".
 *
 * ``order_reference`` is the booking's order-level reference
 * (``Group_Tour_User.order_ref`` — for Ventrata, the order's
 * supplierReference). Same booking scope as ``voucher_link``. Operators
 * use it to build self-service links in the body, e.g. Rockefeller's
 * Manage My Booking URL
 * ``…?reference={{ participant.order_ref|urlencode }}&lastName={{ user.last_name|urlencode }}``.
 */
const CAMPAIGN_VARIABLES_TREE = {
    user: {
        first_name: true,
        last_name: true,
        email: true,
        phone: true,
        voucher_link: 'participant.voucher',
        order_reference: 'participant.order_ref',
    },
    group_tour: {
        name: true,
        date: 'participant.start_datetime.date_us',
        time: 'participant.start_datetime.time_hm_us',
    },
    tour_operator: {
        name: true,
    },
};

/**
 * Operator-facing labels + display order for the top-level variable groups.
 * Keys must match `CAMPAIGN_VARIABLES_TREE` (and therefore the Django
 * template paths). ``user`` reads as "Guest" in the UI because that's how
 * operators think about the recipient. Groups not listed here are appended
 * afterward with a humanized fallback label so the bar never silently drops
 * a category if the tree grows.
 */
const VARIABLE_GROUP_META = [
    { key: 'group_tour', label: 'Tour' },
    { key: 'tour_operator', label: 'Tour operator' },
    { key: 'user', label: 'Guest' },
];

/** "first_name" → "First name" for friendlier menu item labels. */
const humanizeVariableKey = (key) => {
    const spaced = String(key).replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Dotted variable paths referenced in a template source
 * (``"Hi {{ user.first_name|title }}"`` → ``["user.first_name"]``).
 * Filter pipes and ``{% %}`` tag syntax are ignored — this is a hint
 * scanner for availability warnings, not a template parser.
 */
const extractTemplateTokens = (source) => {
    const tokens = [];
    const re = /\{\{\s*([A-Za-z_][\w.]*)/g;
    let match;
    while ((match = re.exec(source || '')) !== null) tokens.push(match[1]);
    return tokens;
};

/**
 * Mode-aware availability notes for the variables a campaign references.
 * Purely client-side: send mode alone determines what the backend render
 * context can fill (see ``_build_recipient_context`` in
 * apiv2/lib/campaign_send.py), so no API call is needed.
 *
 * - Announcement posts one message per tour channel with no individual
 *   recipient: guest-scoped tokens always render blank (warning), and the
 *   tour date/time stub fills from the channel — blank where the channel
 *   has no trip date or resolvable departure time (soft info).
 * - Main Office isn't tied to one tour: tour-scoped tokens anchor to each
 *   recipient's most recent booking (soft info).
 * - Private message has full per-booking scope: no notes.
 *
 * Returns ``[{severity, text}]`` ready to render as MUI Alerts.
 */
const variableAvailabilityNotes = (sendAs, sources) => {
    const tokens = [...new Set(sources.flatMap(extractTemplateTokens))];
    if (tokens.length === 0) return [];
    const matches = (t, prefixes) =>
        prefixes.some((p) => t === p || t.startsWith(`${p}.`));
    const fmt = (list) => list.map((t) => `{{ ${t} }}`).join(', ');
    const notes = [];
    if (sendAs === SEND_AS.ANNOUNCEMENT) {
        const guestScoped = tokens.filter(
            (t) =>
                matches(t, ['user', 'gtu'])
                || (matches(t, ['participant'])
                    && !t.startsWith('participant.start_datetime')),
        );
        if (guestScoped.length > 0) {
            notes.push({
                severity: 'warning',
                text: `Announcements post one message per tour channel, not per guest — ${fmt(guestScoped)} will render blank. Remove guest variables or switch to a per-guest send mode.`,
            });
        }
        if (tokens.some((t) => t.startsWith('participant.start_datetime'))) {
            notes.push({
                severity: 'info',
                text: 'Tour date/time fill in from each channel; channels without a trip date or departure time render them blank.',
            });
        }
    } else if (sendAs === SEND_AS.MAIN_OFFICE) {
        const tourScoped = tokens.filter((t) =>
            matches(t, ['group_tour', 'gtu', 'participant']),
        );
        if (tourScoped.length > 0) {
            notes.push({
                severity: 'info',
                text: `Main Office sends aren't tied to one tour — ${fmt(tourScoped)} will use each recipient's most recent booking, and render blank for recipients without one.`,
            });
        }
    }
    return notes;
};

/**
 * One pill-shaped dropdown for a single variable group. Lists that group's
 * leaf fields and inserts the fully-qualified Django token
 * (``{{ group.field }}``) via ``onInsert``. Assumes a single level of leaves
 * under the group (which matches `CAMPAIGN_VARIABLES_TREE`).
 */
const VariableGroupMenu = ({ label, groupKey, fields, onInsert, disabled }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const keys = React.useMemo(
        () => Object.keys(fields || {}).sort(),
        [fields],
    );
    if (keys.length === 0) return null;
    const close = () => setAnchorEl(null);
    return (
        <>
            <Button
                size="small"
                variant="text"
                disableElevation
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={disabled}
                sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    bgcolor: '#ECECEC',
                    color: 'text.primary',
                    px: 1.5,
                    fontWeight: 500,
                    '&:hover': { bgcolor: '#E0E0E0' },
                    '& .MuiButton-endIcon': { ml: 0.25 },
                }}
            >
                {label}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
                {keys.map((key) => (
                    <MenuItem
                        key={key}
                        onClick={() => {
                            // String leaf = explicit template path (see
                            // CAMPAIGN_VARIABLES_TREE docs); otherwise the
                            // token is the menu path itself.
                            const path =
                                typeof fields[key] === 'string'
                                    ? fields[key]
                                    : `${groupKey}.${key}`;
                            onInsert(`{{ ${path} }}`);
                            close();
                        }}
                    >
                        {humanizeVariableKey(key)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

/**
 * Row of per-group "insert variable" pills shown above the message body.
 * Replaces the single nested "Insert variable" menu with a flatter,
 * scannable layout (one dropdown per group: Campaign / Group tour /
 * Tour operator / Guest). Inserted tokens are identical to before, so the
 * backend render path is unaffected.
 */
const VariableInsertBar = ({ variablesTree, onInsert, disabled }) => {
    const groups = React.useMemo(() => {
        if (!variablesTree || typeof variablesTree !== 'object') return [];
        const known = VARIABLE_GROUP_META.filter((g) => variablesTree[g.key]);
        const extra = Object.keys(variablesTree)
            .filter((k) => !VARIABLE_GROUP_META.some((g) => g.key === k))
            .map((k) => ({ key: k, label: humanizeVariableKey(k) }));
        return [...known, ...extra];
    }, [variablesTree]);

    if (groups.length === 0) return null;

    return (
        <Box>
            <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1 }}
            >
                Insert variable into message:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {groups.map((g) => (
                    <VariableGroupMenu
                        key={g.key}
                        label={g.label}
                        groupKey={g.key}
                        fields={variablesTree[g.key]}
                        onInsert={onInsert}
                        disabled={disabled}
                    />
                ))}
            </Box>
        </Box>
    );
};


/**
 * Click-to-select option tile used by the Purpose picker and the channel
 * picker on the Compose step. Renders a bordered card with an optional
 * leading control (checkbox / radio dot), a bold title, and a muted
 * description. The whole card is the click target; the selected state gets
 * a blue ring + tint via the shared ``optionCard`` styles.
 */
const SelectableCard = ({
    selected,
    onToggle,
    disabled,
    control,
    title,
    description,
}) => {
    const classes = useStyles();
    const className = [
        classes.optionCard,
        selected ? classes.optionCardSelected : '',
        disabled ? classes.optionCardDisabled : '',
    ].filter(Boolean).join(' ');
    return (
        <Box
            className={className}
            role="button"
            aria-pressed={!!selected}
            tabIndex={disabled ? -1 : 0}
            onClick={() => { if (!disabled) onToggle(!selected); }}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggle(!selected);
                }
            }}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
            {control}
            <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={600}>{title}</Typography>
                {description && (
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};


// ---- Delivery-row mutation helpers ------------------------------------
//
// The Channel checkboxes (chat above the body, email below) share the
// same delivery-row plumbing — these pure helpers keep that logic in one
// place so the two render blocks stay thin. `delivery_methods` is a list
// of `Campaign_Delivery_Method` rows; we keep at most one row per method
// id and toggle its ``enabled`` field rather than adding/removing rows
// (the row carries per-method fields like ``email_subject`` that we want
// to preserve when the operator un-checks and re-checks the channel).
const deliveryRowFor = (rows, methodId) =>
    (rows || []).find((r) => Number(r.method) === methodId);

const withDeliveryEnabled = (rows, methodId, enabled) => {
    const next = Array.isArray(rows) ? rows.slice() : [];
    const idx = next.findIndex((r) => Number(r.method) === methodId);
    if (idx === -1) {
        if (!enabled) return next;
        next.push({
            id: null,
            method: methodId,
            sequence: next.length,
            receivers: 'user',
            label: '',
            enabled: true,
        });
    } else {
        next[idx] = { ...next[idx], enabled };
    }
    return next;
};

const withDeliveryPatch = (rows, methodId, patch) => {
    const next = Array.isArray(rows) ? rows.slice() : [];
    const idx = next.findIndex((r) => Number(r.method) === methodId);
    if (idx === -1) {
        next.push({
            id: null,
            method: methodId,
            sequence: next.length,
            receivers: 'user',
            label: '',
            enabled: false,
            ...patch,
        });
    } else {
        next[idx] = { ...next[idx], ...patch };
    }
    return next;
};

// The channel pickers now render as cards on the Compose step's
// "How it's sent" section via ``ChannelCard`` (defined below); the older
// ``FormControlLabel``-based toggles were removed in favour of that
// shared component.

/**
 * Card-styled channel toggle for the Compose step's "How it's sent"
 * section. Wraps the same ``delivery_methods`` row plumbing as the
 * checkbox toggles above, but renders as a click-to-select tile matching
 * the mockup. ``methodId`` is the ``DELIVERY_METHOD`` constant the card
 * controls.
 */
const ChannelCard = ({ methodId, title, description, deliveryMethods, onChange, disabled }) => {
    const row = deliveryRowFor(deliveryMethods, methodId);
    const enabled = !!(row && row.enabled !== false);
    return (
        <SelectableCard
            selected={enabled}
            disabled={disabled}
            onToggle={(next) => onChange(withDeliveryEnabled(deliveryMethods, methodId, next))}
            control={(
                <Checkbox
                    checked={enabled}
                    disabled={disabled}
                    tabIndex={-1}
                    sx={{ p: 0, mt: 0.25 }}
                />
            )}
            title={title}
            description={description}
        />
    );
};

/**
 * Email subject field. Renders directly under the Email checkbox at the
 * top of the Message tab (only when email is enabled) so the inbox-line
 * authoring sits adjacent to its opt-in. The rich-text override expander
 * is a separate component (``EmailOverrideExpander``) and lives lower in
 * the tab — operators author the shared body first, then optionally
 * override for email.
 */
const EmailSubjectField = ({ deliveryMethods, onChange, disabled, forceShowError }) => {
    const emailRow = deliveryRowFor(deliveryMethods, DELIVERY_METHOD.EMAIL);
    const emailEnabled = !!(emailRow && emailRow.enabled !== false);
    const emailSubject = emailRow?.email_subject ?? '';
    const emailSubjectMissing = emailEnabled && !emailSubject.trim();
    // Don't flag the Subject field red the moment the operator checks
    // the Email box — they haven't had a chance to type yet. Only show
    // the error once they've blurred the field at least once, or once
    // the wizard blocked leaving the Compose step for this reason
    // (``forceShowError`` — CP-8076).
    const [subjectTouched, setSubjectTouched] = React.useState(false);
    const showSubjectError = (subjectTouched || forceShowError) && emailSubjectMissing;
    if (!emailEnabled) return null;
    return (
        <TextField
            label="Subject"
            value={emailSubject}
            onChange={(e) =>
                onChange(withDeliveryPatch(deliveryMethods, DELIVERY_METHOD.EMAIL, { email_subject: e.target.value }))
            }
            onBlur={() => setSubjectTouched(true)}
            variant="filled"
            size="small"
            disabled={disabled}
            required
            error={showSubjectError}
            fullWidth
            sx={{ mt: 1 }}
            helperText={
                showSubjectError
                    ? 'Subject is required when Email is enabled.'
                    : ''
            }
        />
    );
};

/**
 * "Override message body for email" expander. Sits lower in the Message
 * tab (after body + translate) so the operator authors the shared body
 * first, then optionally overrides for email. The Email checkbox at the
 * top controls visibility — when unchecked, this returns null.
 */
const EmailOverrideExpander = ({
    deliveryMethods,
    onChange,
    disabled,
    onOpenEmailPreview,
    emailPreviewDisabledReason,
}) => {
    const emailRow = deliveryRowFor(deliveryMethods, DELIVERY_METHOD.EMAIL);
    const emailEnabled = !!(emailRow && emailRow.enabled !== false);
    if (!emailEnabled) return null;
    return (
        <EmailBodyCustomizer
            value={emailRow?.email_message ?? ''}
            onChange={(html) =>
                onChange(withDeliveryPatch(deliveryMethods, DELIVERY_METHOD.EMAIL, { email_message: html }))
            }
            disabled={disabled}
            onOpenPreview={onOpenEmailPreview}
            previewDisabledReason={emailPreviewDisabledReason}
        />
    );
};


/**
 * Collapsible "Customize for email" expander, shown only when the Email
 * channel is enabled. When collapsed (the default), email sends use the
 * shared ``Campaign.message_body`` as plaintext. When expanded, the
 * operator authors a rich HTML body on the Campaign_Delivery_Method's
 * ``email_message`` field. The send pipeline's ``_resolve_method_body_template``
 * already prefers this field when set; no backend changes needed.
 */
const EmailBodyCustomizer = ({
    value,
    onChange,
    disabled,
    onOpenPreview,
    previewDisabledReason,
}) => {
    const hasOverride = typeof value === 'string' && value.trim().length > 0;
    const [expanded, setExpanded] = React.useState(hasOverride);
    // The image-picker dialog runs as a controlled overlay: the Quill
    // toolbar's image button calls ``onInsertImage(insertFn)``; we stash
    // ``insertFn`` and open the dialog. When the operator picks an image
    // (upload or browse), we call the stashed fn to drop the <img> at
    // the cursor where the toolbar button was clicked.
    const [imagePickerOpen, setImagePickerOpen] = React.useState(false);
    const pendingInsertRef = React.useRef(null);
    // Clear-override confirmation. Without this, an operator can wipe the
    // override by select-all-delete in the Quill editor with no
    // protection — and a wiped override silently falls back to the
    // shared Message body as plaintext, which is rarely what they meant.
    // The button + dialog give them a deliberate revert path.
    const [clearConfirmOpen, setClearConfirmOpen] = React.useState(false);

    const handleRequestImage = React.useCallback((insertFn) => {
        pendingInsertRef.current = insertFn;
        setImagePickerOpen(true);
    }, []);

    const handlePickerPick = ({ url, name }) => {
        const insert = pendingInsertRef.current;
        pendingInsertRef.current = null;
        if (typeof insert === 'function') insert(url, name);
    };

    return (
        <Box sx={{ mt: 1.5, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    startIcon={<ChevronRightIcon
                        sx={{
                            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 120ms ease',
                        }}
                    />}
                    sx={{ textTransform: 'none' }}
                >
                    Override message body for email
                </Button>
                {/* Warning-colored chip when the override is set but the
                    expander is collapsed — the operator's eye needs to
                    catch this immediately because the override IS what
                    email recipients will receive (regardless of expander
                    state) and a collapsed neutral-grey chip is too easy
                    to skim past. */}
                {!expanded && hasOverride && (
                    <Chip
                        size="small"
                        color="warning"
                        label="Email override active"
                    />
                )}
            </Box>
            {expanded && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Replaces the shared Message body for email recipients only. Authored in
                        rich HTML (use the image button to insert inline images). When blank,
                        email uses the shared Message body as plaintext.
                    </Typography>
                    <QuillEditor
                        value={value || ''}
                        onChange={(next) => onChange(next || '')}
                        variablesTree={CAMPAIGN_VARIABLES_TREE}
                        containerHeight="180px"
                        onInsertImage={disabled ? undefined : handleRequestImage}
                        // Preview button + Clear override button ride
                        // inside QuillEditor's own outlined-button row,
                        // right next to "Insert variable" — same styling,
                        // same alignment. Preview mirrors the per-method
                        // preview flow already in place for notification
                        // templates; fires ``preview_delivery_method``
                        // against a sample audience row. Gated on saved
                        // + clean record (endpoint reads ``email_message``
                        // from the DB). Clear is destructive enough that
                        // it triggers a confirmation dialog rather than
                        // wiping the override directly.
                        extraButtons={
                            <>
                                {typeof onOpenPreview === 'function' && (
                                    <Tooltip
                                        title={previewDisabledReason || ''}
                                        disableHoverListener={!previewDisabledReason}
                                    >
                                        <span>
                                            <Button
                                                variant="outlined"
                                                onClick={onOpenPreview}
                                                disabled={!!previewDisabledReason || disabled}
                                            >
                                                Preview email
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {hasOverride && (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => setClearConfirmOpen(true)}
                                        disabled={disabled}
                                    >
                                        Clear override
                                    </Button>
                                )}
                            </>
                        }
                    />
                </Box>
            )}
            <InlineImagePickerDialog
                open={imagePickerOpen}
                onClose={() => {
                    pendingInsertRef.current = null;
                    setImagePickerOpen(false);
                }}
                onPick={handlePickerPick}
            />
            <Dialog
                open={clearConfirmOpen}
                onClose={() => setClearConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Clear email body override?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Clearing the override will send the shared Message body as plaintext
                        to email recipients (the rich HTML you authored will be lost). This
                        action only affects email — chat is unchanged.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        onClick={() => {
                            onChange('');
                            setClearConfirmOpen(false);
                        }}
                    >
                        Clear override
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/**
 * Audience-tab "Advanced" expander that hides the ruleset picker behind a
 * collapsible toggle. Most campaigns don't need a per-recipient eligibility
 * rule; surfacing the autocomplete prominently confused operators ("what's a
 * ruleset?"). When a ruleset is already configured, the section auto-expands
 * on mount so the operator can see what's set; when nothing is set, only the
 * "Advanced" button is visible.
 *
 * Mirrors the pattern used by ``EmailBodyCustomizer`` (collapse + chevron +
 * "Override set" chip when collapsed-with-value).
 */
const AdvancedRulesetSection = ({ record, update, readOnly }) => {
    const hasRuleset = !!record.ruleset;
    const [expanded, setExpanded] = React.useState(hasRuleset);
    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    startIcon={
                        <ChevronRightIcon
                            sx={{
                                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease',
                            }}
                        />
                    }
                    sx={{ textTransform: 'none' }}
                >
                    Advanced
                </Button>
                {!expanded && hasRuleset && (
                    <Chip
                        size="small"
                        color="info"
                        label={record.ruleset_name || `Ruleset #${record.ruleset}`}
                    />
                )}
            </Box>
            {expanded && (
                <Box sx={{ mt: 1 }}>
                    <RemoteAutocomplete
                        fullWidth
                        resource="notification_ruleset"
                        urlSuffix={null}
                        label="Eligibility rule (applied per recipient at send time)"
                        variant="filled"
                        size="small"
                        disabled={readOnly}
                        value={
                            record.ruleset
                                ? { id: record.ruleset, name: record.ruleset_name || `Ruleset #${record.ruleset}` }
                                : null
                        }
                        onChange={(_, picked) => {
                            update({
                                ruleset: picked?.id ?? null,
                                ruleset_name: picked?.name ?? null,
                            });
                        }}
                        getOptionLabel={(opt) => (opt ? `${opt.name} (ID: ${opt.id})` : '')}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    />
                    {record.ruleset ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Audience preview below does <strong>not</strong> apply this rule —
                            it runs per recipient at send time. Expect the final send count to
                            be lower than the preview.
                        </Alert>
                    ) : null}
                </Box>
            )}
        </Box>
    );
};

const isRecordDirty = (a, b) => {
    // Compare canonicalized payloads. Prevents the unsaved-changes prompt
    // from firing when normalization just reordered keys.
    return JSON.stringify(a) !== JSON.stringify(b);
};


/**
 * Modal that surfaces the AI-translated body per recipient language so
 * the operator can spot-check what their Spanish-speakers (and other
 * non-source-language recipients) will actually receive before clicking
 * Send. Read-only for v1; if operators consistently want to edit a
 * specific language's translation, override comes next.
 *
 * On open: fetches the campaign's audience-language distribution and
 * surfaces the top 8 by recipient count (with "+ N more" expander for the
 * long tail). Each row lazy-loads the actual translation on first expand
 * — saves an LLM round-trip for languages the operator never inspects.
 * Also exposes a "Preview another language…" input for off-roster langs
 * the operator wants to spot-check even when not represented in the
 * current audience.
 *
 * Backend mirrors the send-time skip logic: when the requested ``lang``
 * matches the body's detected source language, returns the body verbatim
 * (no LLM call) and the row shows "Same as source — no translation".
 */
const TranslationPreviewDialog = ({ open, onClose, campaignId, dataAPI }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [languages, setLanguages] = React.useState([]);
    const [audienceMode, setAudienceMode] = React.useState('user');
    const [loadingList, setLoadingList] = React.useState(false);
    const [previewByLang, setPreviewByLang] = React.useState({});
    const [expandedLangs, setExpandedLangs] = React.useState(() => new Set());
    const [loadingLang, setLoadingLang] = React.useState(null);
    const [customLang, setCustomLang] = React.useState('');

    // Fetch the audience-language distribution on every dialog open so a
    // long-lived editor session sees the *current* audience composition,
    // not a stale snapshot from when they last opened the dialog.
    React.useEffect(() => {
        if (!open || !campaignId || !dataAPI) return undefined;
        let cancelled = false;
        setLanguages([]);
        setPreviewByLang({});
        setExpandedLangs(new Set());
        setCustomLang('');
        setLoadingList(true);
        fetchCampaignAudienceLanguages(dataAPI, campaignId, { all: false })
            .then((data) => {
                if (cancelled) return;
                setLanguages(data?.languages || []);
                setAudienceMode(data?.audience_mode || 'user');
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('fetchCampaignAudienceLanguages failed', err);
                enqueueSnackbar('Could not load audience languages', { variant: 'error' });
            })
            .finally(() => { if (!cancelled) setLoadingList(false); });
        return () => { cancelled = true; };
    }, [open, campaignId, dataAPI, enqueueSnackbar]);

    // Fetch the translation for ``lang`` if we don't have it cached on
    // the client already; no-op otherwise. Used by both the row toggle
    // (when expanding for the first time) and the custom-lang Preview
    // button.
    const loadPreview = React.useCallback(async (lang) => {
        if (!lang || previewByLang[lang]) return;
        setLoadingLang(lang);
        try {
            const data = await previewCampaignTranslation(dataAPI, campaignId, lang);
            setPreviewByLang((prev) => ({ ...prev, [lang]: data }));
        } catch (err) {
            enqueueSnackbar(err?.message || 'Could not load translation', { variant: 'error' });
        } finally {
            setLoadingLang((cur) => (cur === lang ? null : cur));
        }
    }, [previewByLang, dataAPI, campaignId, enqueueSnackbar]);

    // Click on a language row → toggle expanded state and lazily kick
    // off the translation fetch on first open. The translation result
    // stays cached in ``previewByLang`` after collapse, so re-expanding
    // doesn't burn another LLM round-trip.
    const toggleExpanded = React.useCallback((lang) => {
        if (!lang) return;
        setExpandedLangs((prev) => {
            const next = new Set(prev);
            if (next.has(lang)) {
                next.delete(lang);
            } else {
                next.add(lang);
            }
            return next;
        });
        if (!previewByLang[lang]) {
            loadPreview(lang);
        }
    }, [previewByLang, loadPreview]);

    const handleCustomPreview = () => {
        const lang = customLang.trim().toLowerCase();
        if (!lang) return;
        // Custom-lang preview always renders below in a separate panel —
        // we just need the translation in the cache, no expanded-state
        // wiring (that panel has no collapse affordance by design).
        loadPreview(lang);
    };

    // Auto-list: top 8 audience-derived langs by default; "+ N more"
    // expander reveals the long tail (fetches the full list from the
    // backend with ?all=1 the first time the operator clicks expand).
    const visibleLanguages = languages;  // backend already truncates to 8

    if (audienceMode === 'channel') {
        return (
            <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Preview translations</DialogTitle>
                <DialogContent>
                    <Alert severity="info">
                        Translation preview is unavailable for announcement-mode campaigns
                        because they target chat channels rather than individual recipients.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Preview translations</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    What each recipient language will actually see at send time. Mirrors
                    the send pipeline&rsquo;s source-language skip + per-language cache,
                    so previewing here populates the cache for the eventual send.
                </DialogContentText>
                {loadingList && (
                    <Typography variant="caption" color="text.secondary">Loading audience languages…</Typography>
                )}
                {!loadingList && visibleLanguages.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        No audience-language data yet. Use &ldquo;Preview another language&rdquo;
                        below to spot-check anyway.
                    </Alert>
                )}
                {visibleLanguages.map((row) => {
                    // Empty ``lang`` means the recipient has no ``User.lang``
                    // populated. The send pipeline's ``_translate_for_recipient``
                    // short-circuits on empty target and returns the source body
                    // untranslated — so there's nothing to preview for this
                    // bucket; we render a flat, non-expandable row that names
                    // the contract ("source body, no translation") and surfaces
                    // the count so operators can decide whether the upstream
                    // ``User.lang`` data problem is worth fixing.
                    if (!row.lang) {
                        return (
                            <Box
                                key="__no_lang__"
                                sx={{
                                    mb: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography fontWeight={500} sx={{ flex: 1, minWidth: 200 }}>
                                    No language set
                                </Typography>
                                <Chip size="small" label="Source body — no translation" />
                                <Typography variant="caption" color="text.secondary">
                                    {row.count.toLocaleString()} recipients
                                </Typography>
                            </Box>
                        );
                    }
                    const preview = previewByLang[row.lang];
                    const isExpanded = expandedLangs.has(row.lang);
                    const isLoadingThis = loadingLang === row.lang;
                    return (
                        <Box key={row.lang} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, p: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                                onClick={() => toggleExpanded(row.lang)}
                            >
                                <ChevronRightIcon
                                    fontSize="small"
                                    sx={{
                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                        transition: 'transform 120ms ease',
                                    }}
                                />
                                <Typography fontWeight={500} sx={{ flex: 1 }}>
                                    {row.label} ({row.lang})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {row.count.toLocaleString()} recipients
                                </Typography>
                            </Box>
                            {isExpanded && (isLoadingThis || preview) && (
                                <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#FAFBFD' }}>
                                    {isLoadingThis && (
                                        <Typography variant="caption" color="text.secondary">
                                            Translating&hellip;
                                        </Typography>
                                    )}
                                    {preview && (
                                        <>
                                            {preview.source_lang && preview.source_lang === preview.lang && (
                                                <Chip
                                                    size="small"
                                                    label="Same as source — no translation"
                                                    sx={{ mb: 1 }}
                                                />
                                            )}
                                            {/* Subject only renders when the campaign has an
                                                email row with a non-empty subject (backend
                                                returns null otherwise). Labeled so the operator
                                                sees "this is the inbox-line for this language"
                                                rather than two anonymous blocks of text. */}
                                            {preview.subject && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', fontWeight: 600 }}
                                                    >
                                                        Email subject
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ m: 0 }}>
                                                        {preview.subject}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {(preview.subject || preview.mobile_body) && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', fontWeight: 600 }}
                                                >
                                                    Message body
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="body2"
                                                component="pre"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'inherit',
                                                    m: 0,
                                                }}
                                            >
                                                {preview.body}
                                            </Typography>
                                            {/* Mobile version (CP-8018): translated under its
                                                own namespace at send time, so it needs its own
                                                review row — SMS/WhatsApp recipients get this
                                                text, not the body above. Backend returns null
                                                when the campaign has no mobile body. */}
                                            {preview.mobile_body && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', fontWeight: 600 }}
                                                    >
                                                        Mobile version (SMS/WhatsApp)
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        component="pre"
                                                        sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            fontFamily: 'inherit',
                                                            m: 0,
                                                        }}
                                                    >
                                                        {preview.mobile_body}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F5F7FB', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Preview another language not in this campaign&rsquo;s audience
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            variant="filled"
                            placeholder="e.g. es, fr, ja"
                            value={customLang}
                            onChange={(e) => setCustomLang(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCustomPreview();
                            }}
                            sx={{ flex: 1 }}
                            inputProps={{ maxLength: 8 }}
                        />
                        <Button variant="outlined" onClick={handleCustomPreview} disabled={!customLang.trim()}>
                            Preview
                        </Button>
                    </Box>
                    {Object.entries(previewByLang)
                        .filter(([lang]) => !visibleLanguages.some((r) => r.lang === lang))
                        .map(([lang, preview]) => (
                            <Box key={lang} sx={{ mt: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}>
                                <Typography variant="caption" fontWeight={600}>
                                    {preview.label || lang.toUpperCase()} ({lang})
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'inherit',
                                        m: 0,
                                        mt: 0.5,
                                    }}
                                >
                                    {preview.body}
                                </Typography>
                            </Box>
                        ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Send-confirmation dialog with an inline "Send now / Schedule for…" picker.
 *
 * The picker (rather than two separate dialog modes) is the whole point of
 * moving send_timing out of the Settings tab — operators choose at the
 * moment of sending. ``onConfirm(targetStatus, { send_timing, scheduled_at })``
 * is called with the resolved fields; the editor passes those through to
 * ``sendCampaignApi`` which threads them to the backend's /send/ endpoint.
 *
 * The dialog initializes from the campaign's current schedule (if any) so
 * re-opening a scheduled campaign shows the existing time, not a blank.
 */
// Maps the channel key returned by the backend's cap_usage endpoint to
// the campaign's enabled-channels keys, so the dialog only shows cap
// rows for channels the operator selected. Airbnb is intentionally
// omitted from the chat expansion: it rides along server-side as a
// fanout for Airbnb-source guests, but operators will hit the email or
// chat cap long before the Airbnb cap matters in practice, and showing
// a separate "Airbnb" cap row for a channel they didn't pick is noisy
// (and worse, can block confirm when the per-channel cap is tight even
// if the actual fanout would be tiny).
const CAP_CHANNEL_KEYS_FOR_DELIVERY = {
    chat: ['chat'],
    email: ['email'],
    sms: ['sms'],
    whatsapp: ['whatsapp'],
    push: ['push'],
    // Composite methods expand to *every* channel they might route into,
    // so the operator sees every relevant cap in the Send dialog. Over-
    // conservative on display (audience won't all route to one channel)
    // but matches the backend's preflight which checks each cap.
    mobile_phone_number: ['sms', 'whatsapp'],
    chat_if_available_or_mobile_phone_number: ['chat', 'sms', 'whatsapp'],
};

// Display labels for the cost-estimate channel keys returned by
// GET campaign/{id}/cost_estimate/.
const COST_CHANNEL_LABELS = {
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    chat: 'In-app chat',
    email: 'Email',
    push: 'Push',
};

const _fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/**
 * Inline message preview shown at the top of the Send step. Renders a
 * channel tab strip (only the channels the campaign will actually send
 * on) and the authored content for the selected channel — the chat/SMS
 * plaintext body, or the email subject + body. Template variables
 * (``{{ user.first_name }}``) are shown verbatim because they only
 * resolve per-recipient at send time; the footer points operators at
 * "Send test" for the fully personalized, translated render.
 */
const MessagePreview = ({
    record,
    chatEnabled,
    mobileEnabled,
    chatOrMobileEnabled,
    emailEnabled,
    emailRow,
    onSendTest,
    canSendTest,
}) => {
    const tabs = [
        chatEnabled ? { key: 'chat', label: 'In-app chat' } : null,
        mobileEnabled ? { key: 'mobile', label: 'SMS / WhatsApp' } : null,
        // Label matches the compose-step channel card ("In-app Chat, else
        // SMS/WhatsApp") so the same channel doesn't read as a second,
        // ambiguous "Chat" tab next to In-app chat.
        chatOrMobileEnabled ? { key: 'chat_or_mobile', label: 'In-app chat, else SMS/WhatsApp' } : null,
        emailEnabled ? { key: 'email', label: 'Email' } : null,
    ].filter(Boolean);

    const [active, setActive] = React.useState(tabs[0]?.key || null);
    // Keep the active tab valid as channels are toggled on the Compose
    // step (the operator can flip channels and step back here).
    React.useEffect(() => {
        if (tabs.length === 0) {
            if (active !== null) setActive(null);
            return;
        }
        if (!tabs.some((t) => t.key === active)) {
            setActive(tabs[0].key);
        }
    }, [tabs, active]);

    if (tabs.length === 0) {
        return (
            <Alert severity="info">
                No channels selected yet — pick at least one channel on the Compose step.
            </Alert>
        );
    }

    const body = (record.message_body || '').trim();
    const mobileBody = (record.mobile_message_body || '').trim();
    const isEmail = active === 'email';
    const emailSubject = (emailRow?.email_subject ?? '').trim();
    const emailOverrideSet = !!(emailRow?.email_message ?? '').trim();
    // SMS/WhatsApp recipients get the mobile variant when one is authored
    // (CP-8018). The standalone mobile tab swaps the body wholesale. The
    // composite "in-app chat, else SMS/WhatsApp" tab reaches two audiences
    // with two different bodies, so when a mobile version exists it shows
    // BOTH, each labelled with who receives it (render below) rather than
    // foregrounding one and burying the other.
    const displayBody = active === 'mobile' && mobileBody ? mobileBody : body;
    const showDualBody = active === 'chat_or_mobile' && !!mobileBody;

    return (
        <Box>
            <Tabs
                value={active}
                onChange={(e, next) => setActive(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minHeight: 40 }}
            >
                {tabs.map((t) => (
                    <Tab key={t.key} value={t.key} label={t.label} sx={{ minHeight: 40, textTransform: 'none' }} />
                ))}
            </Tabs>
            <Box
                sx={{
                    p: 2,
                    bgcolor: '#F7F8FB',
                    borderRadius: 2,
                    border: '1px solid #ECEFF4',
                    minHeight: 96,
                }}
            >
                {isEmail && (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {emailSubject || (
                            <span style={{ color: '#9AA0A6' }}>(no subject yet)</span>
                        )}
                    </Typography>
                )}
                {isEmail && emailOverrideSet ? (
                    <Alert severity="info" sx={{ mb: 0 }}>
                        A rich HTML email override is set — email recipients see the formatted
                        version. Use <strong>Send test</strong> to preview the exact email.
                    </Alert>
                ) : showDualBody ? (
                    // This channel splits its audience: recipients with the app
                    // get in-app chat (the full body); everyone else gets
                    // SMS/WhatsApp (the mobile version). Show both so the
                    // operator sees exactly what each half receives.
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            In-app chat recipients
                        </Typography>
                        {body ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {body}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No message body yet.</Typography>
                        )}
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #D5DAE3' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                SMS / WhatsApp recipients
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {mobileBody}
                            </Typography>
                        </Box>
                    </Box>
                ) : displayBody ? (
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                        {displayBody}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No message body yet.
                    </Typography>
                )}
            </Box>
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                    Variables like <code>{'{{ user.first_name }}'}</code> are filled in per
                    recipient and translated at send time.
                </Typography>
                {onSendTest && (
                    <Button size="small" variant="outlined" onClick={onSendTest} disabled={!canSendTest}>
                        Send test
                    </Button>
                )}
            </Box>
        </Box>
    );
};

/**
 * Inline "when to send" panel for the Send step — the timing choice
 * (Send now / Schedule), the scheduled-time picker, and the per-channel
 * daily-cap preflight that used to live in the Send dialog. Folding it
 * inline matches the designer's single-page Send step; the cap logic is
 * unchanged (still blocks the send button when the audience exceeds a
 * channel's remaining cap for today).
 *
 * ``confirmDisabled`` / ``confirmDisabledReason`` come from the parent
 * (unsaved changes, missing email subject, etc.); the panel ANDs them
 * with its own cap/timing checks.
 */
const SendStepPanel = ({
    draftActions,
    onConfirm,
    onCancelSchedule,
    onBack,
    saving,
    initialScheduledAt,
    initialMode,
    recipientCount,
    deliveryChannelKeys,
    campaignId,
    dataAPI,
    confirmDisabled,
    confirmDisabledReason,
    promotional,
}) => {
    const classes = useStyles();
    const [mode, setMode] = React.useState(initialMode || CAMPAIGN_TIMING.NOW);
    const [scheduledAtLocal, setScheduledAtLocal] = React.useState(
        initialScheduledAt ? moment(initialScheduledAt).format('YYYY-MM-DDTHH:mm') : '',
    );
    const [capUsage, setCapUsage] = React.useState(null);
    const [costEstimate, setCostEstimate] = React.useState(null);
    const [unreachable, setUnreachable] = React.useState(null);
    // datetime-local is browser-local; we convert to UTC ISO for the API.
    // Surface the zone so operators don't assume UTC or customer local time.
    const localTimeNote = operatorLocalTimeLabel('your local time');
    const scheduledTimeHelper = `${localTimeNote.charAt(0).toUpperCase()}${localTimeNote.slice(1)} — note that this may be different from the customer's time zone.`;

    // Pull fresh cap usage when the panel mounts (operator reached the
    // Send step). The backend counts ``Campaign_Attribution`` rows over
    // the current UTC day, so a long-lived editor tab still sees accurate
    // "remaining today".
    React.useEffect(() => {
        if (!dataAPI) return undefined;
        let cancelled = false;
        setCapUsage(null);
        fetchCampaignCapUsage(dataAPI).then((data) => {
            if (!cancelled) setCapUsage(data);
        });
        return () => { cancelled = true; };
    }, [dataAPI]);

    // Promotional campaigns only (backend rollout phase 4): the pricing summary -- the messages at standard rates plus
    // the campaign fee ($0.02 per recipient). Reads the persisted campaign (audience + body + channels), so it's
    // meaningful only for a saved campaign -- which is also the only state from which a send is allowed (confirm is
    // gated on !dirty && hasPersistentRecord upstream). Null (fetch failed, or an un-modeled announcement/main-office
    // campaign) simply hides the rows; it never blocks confirm.
    React.useEffect(() => {
        if (!promotional || !dataAPI || !campaignId) { setCostEstimate(null); return undefined; }
        let cancelled = false;
        setCostEstimate(null);
        fetchCampaignCostEstimate(dataAPI, campaignId).then((data) => {
            if (!cancelled) setCostEstimate(data);
        });
        return () => { cancelled = true; };
    }, [promotional, dataAPI, campaignId]);

    // Guests this campaign won't message on WhatsApp because they replied STOP or have no WhatsApp: no fallback,
    // so the operator hears it before sending (backend switch campaign_skip_unreachable_whatsapp; off: nothing).
    React.useEffect(() => {
        if (!dataAPI || !campaignId) { setUnreachable(null); return undefined; }
        let cancelled = false;
        setUnreachable(null);
        fetchCampaignWhatsAppUnreachable(dataAPI, campaignId).then((data) => {
            if (!cancelled) setUnreachable(data?.enabled ? data.counts : null);
        });
        return () => { cancelled = true; };
    }, [dataAPI, campaignId]);
    const optedOut = Number(unreachable?.whatsapp_opted_out || 0);
    const noWhatsApp = Number(unreachable?.no_whatsapp || 0);
    const unreachableTotal = optedOut + noWhatsApp;

    // Essential campaigns with promotional content are blocked (backend switch campaign_essential_promo_check in
    // block mode; otherwise ``enabled`` is false and nothing shows). The first reading of a version takes a few
    // seconds; the backend checks again at send, so a failed fetch never holds the step.
    const { enqueueSnackbar } = useSnackbar();
    const [promo, setPromo] = React.useState(null);
    const [promoLoading, setPromoLoading] = React.useState(false);
    const [appealOpen, setAppealOpen] = React.useState(false);
    const [appealNote, setAppealNote] = React.useState('');
    const [promoBusy, setPromoBusy] = React.useState(false);
    React.useEffect(() => {
        if (!dataAPI || !campaignId) { setPromo(null); return undefined; }
        let cancelled = false;
        setPromo(null);
        setPromoLoading(true);
        checkCampaignPromo(dataAPI, campaignId)
            .catch(() => ({ enabled: false }))
            .then((data) => {
                if (cancelled) return;
                setPromo(data?.enabled ? data : null);
                setPromoLoading(false);
            });
        return () => { cancelled = true; };
    }, [dataAPI, campaignId]);
    const promoBlocked = !!promo?.blocked;

    const handleAppeal = async () => {
        setPromoBusy(true);
        try {
            setPromo(await appealCampaignPromo(dataAPI, campaignId, appealNote));
            setAppealOpen(false);
            enqueueSnackbar('Sent to TourOptima. We\'ll get back to you.', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(e.message, { variant: 'error' });
        } finally {
            setPromoBusy(false);
        }
    };
    const handleOverride = async () => {
        setPromoBusy(true);
        try {
            setPromo(await overrideCampaignPromo(dataAPI, campaignId));
            enqueueSnackbar('Allowed. This version of the campaign can be sent.', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(e.message, { variant: 'error' });
        } finally {
            setPromoBusy(false);
        }
    };

    // Channels the campaign will actually try to send on. (Airbnb fanout
    // rides along server-side when chat is on, but is intentionally not
    // surfaced as a separate cap row here — see comment on
    // ``CAP_CHANNEL_KEYS_FOR_DELIVERY``.)
    const channelKeysToShow = React.useMemo(() => {
        if (!deliveryChannelKeys || deliveryChannelKeys.length === 0) return [];
        const out = new Set();
        for (const key of deliveryChannelKeys) {
            for (const expanded of (CAP_CHANNEL_KEYS_FOR_DELIVERY[key] || [key])) {
                out.add(expanded);
            }
        }
        return Array.from(out);
    }, [deliveryChannelKeys]);

    // Per-channel rows + over-cap detection. ``remaining: null`` means
    // unlimited; never blocks. ``audience > remaining`` blocks confirm
    // and the row renders red.
    const capRows = React.useMemo(() => {
        if (!capUsage?.channels || channelKeysToShow.length === 0) return [];
        return channelKeysToShow.map((key) => {
            const row = capUsage.channels[key];
            if (!row) return null;
            const audience = Number(recipientCount) || 0;
            const overCap = row.remaining != null && audience > row.remaining;
            return { key, ...row, overCap };
        }).filter(Boolean);
    }, [capUsage, channelKeysToShow, recipientCount]);

    const blockedByCap = capRows.some((r) => r.overCap);

    const isScheduled = mode === CAMPAIGN_TIMING.SCHEDULED;
    const scheduledIso = scheduledAtLocal && moment(scheduledAtLocal).isValid()
        ? moment(scheduledAtLocal).toISOString()
        : null;
    const canConfirm = (isScheduled ? !!scheduledIso : true)
        && !blockedByCap
        && !promoLoading
        && !promoBlocked
        && !confirmDisabled;

    const handleConfirm = () => {
        if (isScheduled) {
            onConfirm(CAMPAIGN_STATUS.SCHEDULED, {
                send_timing: CAMPAIGN_TIMING.SCHEDULED,
                scheduled_at: scheduledIso,
            });
        } else {
            onConfirm(CAMPAIGN_STATUS.SENT, { send_timing: CAMPAIGN_TIMING.NOW });
        }
    };

    return (
        <>
            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                    <Typography className={classes.cardTitle}>When should we send it?</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <SelectableCard
                            selected={!isScheduled}
                            onToggle={() => setMode(CAMPAIGN_TIMING.NOW)}
                            control={<Radio checked={!isScheduled} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                            title="Send now"
                            description="Fire as soon as you confirm."
                        />
                        <SelectableCard
                            selected={isScheduled}
                            onToggle={() => setMode(CAMPAIGN_TIMING.SCHEDULED)}
                            control={<Radio checked={isScheduled} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                            title="Schedule"
                            description={`Pick a future date and time in ${localTimeNote}.`}
                        />
                    </Box>
                    {isScheduled && (
                        <TextField
                            label="Scheduled time"
                            type="datetime-local"
                            value={scheduledAtLocal}
                            onChange={(e) => setScheduledAtLocal(e.target.value)}
                            variant="filled"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText={scheduledTimeHelper}
                            sx={{ mt: 2 }}
                        />
                    )}
                    {capRows.length > 0 && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F5F7FB', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Daily caps
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {capRows.map((row) => (
                                    <Typography
                                        key={row.key}
                                        variant="caption"
                                        sx={{
                                            color: row.overCap ? 'error.main' : 'text.secondary',
                                            fontWeight: row.overCap ? 600 : 400,
                                        }}
                                    >
                                        {row.label}: {row.used} / {row.cap === 0 ? '∞' : row.cap} used today
                                        {row.remaining != null && ` (${row.remaining.toLocaleString()} remaining)`}
                                        {row.overCap && ' — over cap'}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {unreachableTotal > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {unreachableTotal.toLocaleString()} {unreachableTotal === 1 ? 'guest' : 'guests'} won&apos;t
                            {' '}get this on WhatsApp:{' '}
                            {[
                                optedOut > 0 && `${optedOut.toLocaleString()} replied STOP`,
                                noWhatsApp > 0 && `${noWhatsApp.toLocaleString()} ${noWhatsApp === 1 ? "doesn't" : "don't"} have WhatsApp`,
                            ].filter(Boolean).join(', ')}
                            . They&apos;ll be listed in the campaign&apos;s results so you can reach them another way.
                        </Alert>
                    )}
                    {promotional && costEstimate?.total && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F5F7FB', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Estimated cost
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {Object.entries(costEstimate.channels || {}).map(([key, val]) => {
                                    const label = COST_CHANNEL_LABELS[key] || key;
                                    if (val?.note === 'included') {
                                        return (
                                            <Typography key={key} variant="caption" color="text.secondary">
                                                {label}: included
                                            </Typography>
                                        );
                                    }
                                    if (val?.expected == null) return null;
                                    return (
                                        <Typography key={key} variant="caption" color="text.secondary">
                                            {label}: ~{_fmtUsd(val.expected)}
                                            {val.reachable != null && ` (${val.reachable.toLocaleString()} recipients)`}
                                        </Typography>
                                    );
                                })}
                                {costEstimate.campaign_fee && (
                                    <Tooltip title="Charged once for each guest this campaign reaches, whichever channels it uses. Guests it doesn't reach aren't charged, so this is the most it can be.">
                                        <Typography variant="caption" color="text.secondary">
                                            Campaign fee: up to {_fmtUsd(costEstimate.campaign_fee.expected)}
                                            {` (${_fmtUsd(costEstimate.campaign_fee.rate)} × ${Number(costEstimate.campaign_fee.recipients || 0).toLocaleString()} recipients)`}
                                        </Typography>
                                    </Tooltip>
                                )}
                                <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.25 }}>
                                    Estimated total:{' '}
                                    {costEstimate.total.max > costEstimate.total.expected
                                        ? `${_fmtUsd(costEstimate.total.expected)}–${_fmtUsd(costEstimate.total.max)}`
                                        : `~${_fmtUsd(costEstimate.total.expected)}`}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.68rem' }}>
                                Messages are estimated at standard rates; the final cost depends on each message&apos;s length and who it reaches.
                            </Typography>
                        </Box>
                    )}
                    {blockedByCap && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            Audience exceeds today&rsquo;s remaining cap on one or more channels.
                            Schedule for tomorrow, reduce the audience, or ask an admin to raise the cap.
                        </Alert>
                    )}
                    {promoLoading && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="text.secondary">Checking the message…</Typography>
                        </Box>
                    )}
                    {promoBlocked && (
                        <Alert
                            severity="error"
                            sx={{ mt: 2 }}
                            action={(
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {!promo.appealed && (
                                        <Button color="inherit" size="small" onClick={() => setAppealOpen(true)} disabled={promoBusy}>
                                            Contest this
                                        </Button>
                                    )}
                                    {promo.can_override && (
                                        <Button color="inherit" size="small" onClick={handleOverride} disabled={promoBusy}>
                                            Allow anyway
                                        </Button>
                                    )}
                                </Box>
                            )}
                        >
                            {promo.message}
                            {promo.phrases?.length > 0 && (
                                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                    Flagged: {promo.phrases.map((p) => `“${p}”`).join(', ')}
                                </Box>
                            )}
                            {promo.appealed && (
                                <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                                    You contested this. TourOptima will get back to you.
                                </Box>
                            )}
                        </Alert>
                    )}
                    {promo?.overridden && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            TourOptima allowed this version of the campaign. Editing the message will check it again.
                        </Alert>
                    )}
                </CardContent>
            </Card>
            <Dialog open={appealOpen} onClose={() => !promoBusy && setAppealOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Contest this decision</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        We&apos;ll send this campaign to the TourOptima team to review. If you&apos;d like, tell us why it&apos;s
                        essential information for your guests.
                    </DialogContentText>
                    <TextField
                        label="Note (optional)"
                        value={appealNote}
                        onChange={(e) => setAppealNote(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        variant="filled"
                        inputProps={{ maxLength: 2000 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAppealOpen(false)} disabled={promoBusy}>Cancel</Button>
                    <Button variant="contained" onClick={handleAppeal} disabled={promoBusy}>Send</Button>
                </DialogActions>
            </Dialog>
            <Box className={classes.stepActions}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                    >
                        Back
                    </Button>
                    {onCancelSchedule && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={onCancelSchedule}
                            disabled={saving}
                        >
                            Cancel scheduled send
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
                    {draftActions}
                    <Tooltip
                        title={confirmDisabledReason || ''}
                        disableHoverListener={!confirmDisabledReason}
                    >
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirm}
                                disabled={saving || !canConfirm}
                            >
                                {isScheduled ? 'Schedule' : 'Send now'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </>
    );
};

/**
 * Small dialog behind the header's "Send test" button. The operator picks a
 * destination — their email (pre-filled) or a phone number (SMS for US
 * numbers, WhatsApp for international) — and the actual send is delegated to
 * ``onSend({ channel, email, phone })``. Test sends never touch billing /
 * caps / metrics.
 */
const SendTestDialog = ({ open, onClose, defaultEmail, onSend, sending, mobileVersionSet }) => {
    const [channel, setChannel] = React.useState('email');
    const [email, setEmail] = React.useState(defaultEmail || '');
    const [phone, setPhone] = React.useState('');
    React.useEffect(() => {
        if (open) {
            setChannel('email');
            setEmail(defaultEmail || '');
            setPhone('');
        }
    }, [open, defaultEmail]);
    const isPhone = channel === 'phone';
    const value = (isPhone ? phone : email).trim();
    const submit = () => {
        if (!value || sending) return;
        onSend({ channel, email: email.trim(), phone: phone.trim() });
    };
    return (
        <Dialog open={!!open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Send a test</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    We&rsquo;ll send a copy of this campaign so you can proofread it before
                    sending for real. Tests don&rsquo;t count toward delivery, billing, or
                    daily caps.
                </DialogContentText>
                <RadioGroup
                    row
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    sx={{ mb: 1 }}
                >
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="phone" control={<Radio />} label="Phone (SMS / WhatsApp)" />
                </RadioGroup>
                {isPhone ? (
                    <TextField
                        label="Send test to"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                        variant="filled"
                        fullWidth
                        autoFocus
                        placeholder="+14155551234"
                        helperText={
                            'International format, e.g. +14155551234. US (+1) numbers get SMS; others get WhatsApp.'
                            + ' WhatsApp tests only reach phones that have messaged your WhatsApp number in the last 24 hours.'
                            + (mobileVersionSet ? ' Sends the mobile version of the message.' : '')
                        }
                    />
                ) : (
                    <TextField
                        label="Send test to"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                        variant="filled"
                        fullWidth
                        autoFocus
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={submit}
                    disabled={sending || !value}
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {sending ? 'Sending…' : 'Send test'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CampaignForm = ({ isNew }) => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { campaignId } = useParams();
    const { hasPerm } = React.useContext(ContextStore);
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();

    const [record, setRecord] = React.useState(null);
    const [originalRecord, setOriginalRecord] = React.useState(null);
    const [loaded, setLoaded] = React.useState(false);
    const [notFound, setNotFound] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    // Deep-linkable initial step. The campaigns list "Campaign Analytics"
    // action navigates here with ?step=analytics; honor any known step key
    // (falling back to Compose for garbage). Read once on mount — the
    // stepper drives navigation from there.
    const [step, setStep] = React.useState(() => {
        const requested = new URLSearchParams(location.search).get('step');
        const known = [STEP_COMPOSE, STEP_AUDIENCE, STEP_SEND, STEP_ANALYTICS];
        return known.includes(requested) ? requested : STEP_COMPOSE;
    });
    const messageBodyRef = React.useRef(null);
    // Second content box (CP-8018): optional shorter variant for the mobile
    // channels. Needs its own ref so Insert variable targets the right field.
    const mobileMessageBodyRef = React.useRef(null);
    // Latest audience-preview count, lifted from the audience builder so the
    // Send dialog can surface "X recipients" before the operator confirms.
    const [audienceCount, setAudienceCount] = React.useState(null);
    const [filterOptions, setFilterOptions] = React.useState(null);
    const [filterOptionsError, setFilterOptionsError] = React.useState(null);
    const [translationPreviewOpen, setTranslationPreviewOpen] = React.useState(false);
    const [emailPreviewOpen, setEmailPreviewOpen] = React.useState(false);
    // CP-8076: required-field validation is quiet until the operator tries
    // to leave a step with missing fields — then the step's fields render
    // their error state and navigation is blocked until they're filled.
    // Kept per-step so a blocked Compose doesn't paint Audience red too.
    const [showComposeErrors, setShowComposeErrors] = React.useState(false);
    const [showAudienceErrors, setShowAudienceErrors] = React.useState(false);
    const [testDialogOpen, setTestDialogOpen] = React.useState(false);
    const [sendingTest, setSendingTest] = React.useState(false);
    const [cancelScheduleOpen, setCancelScheduleOpen] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        if (isNew) {
            const blank = createBlankCampaign();
            setRecord(blank);
            setOriginalRecord(blank);
            setNotFound(false);
            setLoaded(true);
            return undefined;
        }
        if (!campaignId) {
            setLoaded(true);
            return undefined;
        }
        setLoaded(false);
        fetchCampaignOne(dataAPI, campaignId)
            .then((existing) => {
                if (cancelled) return;
                if (existing) {
                    setRecord(existing);
                    setOriginalRecord(existing);
                    setNotFound(false);
                } else {
                    setRecord(null);
                    setNotFound(true);
                }
            })
            .catch((err) => {
                console.error(err);
                if (!cancelled) {
                    enqueueSnackbar('Could not load campaign', { variant: 'error' });
                    setRecord(null);
                    setNotFound(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [isNew, campaignId, dataAPI, enqueueSnackbar]);

    // Promotional comes back operator by operator (backend rollout phase 4).
    const [promotionalEnabled, setPromotionalEnabled] = React.useState(false);
    React.useEffect(() => {
        let cancelled = false;
        fetchCampaignFeatures(dataAPI).then((features) => {
            if (!cancelled) setPromotionalEnabled(!!features?.promotional);
        });
        return () => { cancelled = true; };
    }, [dataAPI]);

    React.useEffect(() => {
        let cancelled = false;
        fetchCampaignFilterOptions(dataAPI)
            .then((opts) => {
                if (!cancelled) setFilterOptions(opts);
            })
            .catch((err) => {
                console.warn('Could not load filter options', err);
            });
        return () => {
            cancelled = true;
        };
    }, [dataAPI]);

    // ---- Polling while a send is in flight --------------------------------
    //
    // The /send/ endpoint dispatches the actual fanout to a Celery worker
    // and returns immediately with status=ongoing. Without polling the
    // editor would stay stuck on "Ongoing" until the operator refreshes;
    // worse, a quick-finishing send (1 recipient = ~ms) leaves them
    // wondering whether it ever fired. Poll every 3 seconds while the
    // record is in ongoing status, with a 5-minute ceiling, and refresh
    // the local record from the server when the worker flips the status
    // to sent / failed. The ongoing status is read-only on the form so
    // overwriting record is safe (no unsaved-changes conflict).
    const recordId = record?.id;
    const recordStatus = record?.status;
    React.useEffect(() => {
        if (recordStatus !== CAMPAIGN_STATUS.ONGOING || !recordId) return undefined;
        let cancelled = false;
        let timer = null;
        const pollInterval = 3000;
        const maxPolls = 100; // ~5 minutes ceiling before we ask the operator to refresh
        let polls = 0;
        const tick = async () => {
            if (cancelled) return;
            polls += 1;
            try {
                const fresh = await fetchCampaignOne(dataAPI, recordId);
                if (cancelled) return;
                if (fresh && fresh.status && fresh.status !== CAMPAIGN_STATUS.ONGOING) {
                    setRecord(fresh);
                    setOriginalRecord(fresh);
                    const variant = fresh.status === CAMPAIGN_STATUS.FAILED ? 'error' : 'success';
                    enqueueSnackbar(`Campaign → ${fresh.status}`, { variant });
                    return;
                }
            } catch (err) {
                console.warn('campaign status poll failed', err);
            }
            if (!cancelled && polls < maxPolls) {
                timer = setTimeout(tick, pollInterval);
            }
        };
        timer = setTimeout(tick, pollInterval);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [recordStatus, recordId, dataAPI, enqueueSnackbar]);

    const update = React.useCallback((patch) => setRecord((r) => ({ ...r, ...patch })), []);

    // Purpose switch. Channel options are purpose-gated: Marketing shows
    // Chat + Mobile (SMS/WhatsApp); Transactional shows the priority-chain
    // Chat-or-Mobile. When purpose changes, clear the now-hidden delivery
    // rows so the saved state matches what the operator can see (otherwise
    // a previously-checked option could keep firing silently). Switching to
    // Transactional also auto-enables Chat-or-Mobile (the only non-email
    // channel transactional exposes). The reverse leaves Chat + Mobile
    // unchecked because either-or-both is a real decision we don't pre-pick.
    const applyPurpose = React.useCallback((nextPurpose) => {
        setRecord((r) => {
            if (!r) return r;
            let rows = r.delivery_methods || [];
            if (nextPurpose === CAMPAIGN_PURPOSE.TRANSACTIONAL) {
                rows = withDeliveryEnabled(rows, DELIVERY_METHOD.CHAT, false);
                rows = withDeliveryEnabled(rows, DELIVERY_METHOD.MOBILE_PHONE_NUMBER, false);
                rows = withDeliveryEnabled(rows, DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER, true);
            } else {
                rows = withDeliveryEnabled(rows, DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER, false);
            }
            return { ...r, purpose: nextPurpose, delivery_methods: rows };
        });
    }, []);

    // Splice a Django template token (e.g. "{{ user.first_name }}") into
    // a message-body field at the current cursor. The TextFields are
    // multiline so the underlying node is an HTMLTextAreaElement — we read
    // selectionStart/End off it directly. Falls back to appending when the
    // ref isn't mounted yet.
    const insertIntoBodyField = React.useCallback((field, ref, token) => {
        setRecord((r) => {
            if (!r) return r;
            const current = r[field] || '';
            const el = ref.current;
            if (!el || typeof el.selectionStart !== 'number') {
                return { ...r, [field]: current + token };
            }
            const start = el.selectionStart;
            const end = el.selectionEnd;
            const next = current.slice(0, start) + token + current.slice(end);
            // Restore caret after the inserted token on the next tick (after
            // React reconciles the new value into the textarea).
            const caret = start + token.length;
            window.requestAnimationFrame(() => {
                if (ref.current) {
                    ref.current.focus();
                    ref.current.setSelectionRange(caret, caret);
                }
            });
            return { ...r, [field]: next };
        });
    }, []);
    const insertIntoMessageBody = React.useCallback(
        (token) => insertIntoBodyField('message_body', messageBodyRef, token),
        [insertIntoBodyField],
    );
    const insertIntoMobileMessageBody = React.useCallback(
        (token) => insertIntoBodyField('mobile_message_body', mobileMessageBodyRef, token),
        [insertIntoBodyField],
    );

    const goBack = () => history.push(CAMPAIGNS_LIST_URL);

    const readOnlyByStatus = record && CAMPAIGN_READ_ONLY_STATUSES.includes(record.status);
    const hasWritePerm = hasPerm('campaign', 'write');
    const readOnly = !hasWritePerm || readOnlyByStatus;

    const dirty = Boolean(record && originalRecord && isRecordDirty(record, originalRecord));
    const hasPersistentRecord = Boolean(record?.id);
    const normalizedForSave = record;

    // Analytics only exists once a campaign has been sent (or is mid-send).
    // Append it to the stepper conditionally, and coerce the active step
    // back to Send if we somehow land on Analytics before it's available
    // (e.g. a stale ?step=analytics deep link into a still-draft campaign).
    const showAnalytics = Boolean(record && ANALYTICS_STATUSES.includes(record.status));
    const visibleSteps = showAnalytics ? [...STEPS, ANALYTICS_STEP] : STEPS;
    const activeStep = step === STEP_ANALYTICS && !showAnalytics ? STEP_SEND : step;

    const onSave = async () => {
        if (!normalizedForSave) return;
        const creating = isNew || normalizedForSave.id == null;
        setSaving(true);
        try {
            const saved = await saveCampaign(dataAPI, normalizedForSave, creating);
            if (saved) {
                setRecord(saved);
                setOriginalRecord(saved);
                enqueueSnackbar('Campaign saved', { variant: 'success' });
                if (creating && saved.id) {
                    history.replace(`/notifications/campaigns/${saved.id}`);
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Could not save campaign', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const doSendFlow = async (target, options = {}) => {
        if (!record?.id) return;
        setSaving(true);
        try {
            const { campaign: updated, sendSummary } = await sendCampaignApi(
                dataAPI,
                record.id,
                target,
                options,
            );
            if (updated) {
                setRecord(updated);
                setOriginalRecord(updated);
                let variant = updated.status === CAMPAIGN_STATUS.FAILED ? 'error' : 'success';
                // Belt-and-suspenders: status should always be present on a
                // 2xx response from the backend, but if a future code path
                // ever omits it, don't render the literal "undefined".
                let label = updated.status
                    ? `Campaign → ${updated.status}`
                    : 'Campaign updated';
                if (sendSummary && typeof sendSummary === 'object') {
                    if (sendSummary.message) {
                        label = sendSummary.message;
                    }
                    // Mixed success: status is "sent" but errors > 0.
                    if (sendSummary.errors > 0 && updated.status === CAMPAIGN_STATUS.SENT) {
                        variant = 'warning';
                    }
                }
                enqueueSnackbar(label, { variant });
                // A send/retry that actually dispatched (or kicked off an
                // async send) exposes the analytics step — jump straight to
                // it so the operator sees results, matching the prior
                // inline-report behavior. Scheduling stays on the Send step.
                if (ANALYTICS_STATUSES.includes(updated.status)) {
                    setStep(STEP_ANALYTICS);
                }
            }
        } catch (err) {
            console.error(err);
            // sendCampaignApi now throws an Error carrying the backend's
            // ``message`` field for 4xx/5xx responses (precondition fails,
            // invalid scheduled_at, illegal state transition, etc.).
            // Surface it verbatim so the operator can act on it instead of
            // seeing the generic "Could not update campaign status".
            const detail = err?.message && !err.message.startsWith('Failed')
                ? err.message
                : 'Could not update campaign status';
            enqueueSnackbar(detail, { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Cancel a scheduled send: revert the campaign to ``draft`` via the
    // same /send/ endpoint (target_status="draft"). Once it leaves the
    // ``scheduled`` status the Celery beat task stops picking it up, so
    // the send never fires; the operator can edit and re-schedule later.
    // Only meaningful while the campaign is still ``scheduled`` (an
    // already-claimed/ongoing send can't be pulled back).
    const doCancelSchedule = async () => {
        setCancelScheduleOpen(false);
        await doSendFlow(CAMPAIGN_STATUS.DRAFT);
    };

    // Header "Send test" flow. Test sends require a saved campaign (the
    // backend renders the body straight from the DB), so we save first when
    // there are unsaved changes, then deliver a proof copy to the operator
    // by email or by phone (SMS/WhatsApp).
    const doSendTest = async ({ channel = 'email', email = '', phone = '' } = {}) => {
        const isPhone = channel === 'phone';
        const target = (isPhone ? phone : email).trim();
        if (!target) return;
        setSendingTest(true);
        try {
            let campaignRecord = record;
            if (!campaignRecord?.id || dirty) {
                const creating = isNew || campaignRecord?.id == null;
                campaignRecord = await saveCampaign(dataAPI, record, creating);
                if (campaignRecord) {
                    setRecord(campaignRecord);
                    setOriginalRecord(campaignRecord);
                    if (creating && campaignRecord.id) {
                        history.replace(`/notifications/campaigns/${campaignRecord.id}`);
                    }
                }
            }
            if (!campaignRecord?.id) {
                enqueueSnackbar('Save the campaign before sending a test', { variant: 'error' });
                return;
            }
            const res = await sendCampaignTestApi(dataAPI, campaignRecord.id, {
                channel,
                email: email.trim(),
                phone: phone.trim(),
            });
            enqueueSnackbar(
                res?.message || (isPhone ? `Test message sent to ${target}` : `Test email sent to ${target}`),
                { variant: 'success' },
            );
            setTestDialogOpen(false);
        } catch (err) {
            console.error(err);
            const detail = err?.message && !err.message.startsWith('Failed')
                ? err.message
                : 'Could not send the test';
            enqueueSnackbar(detail, { variant: 'error' });
        } finally {
            setSendingTest(false);
        }
    };

    const onDuplicate = async () => {
        if (!record?.id) return;
        try {
            const clone = await duplicateCampaignApi(dataAPI, record.id);
            if (clone?.id) {
                enqueueSnackbar('Campaign duplicated', { variant: 'success' });
                history.push(`/notifications/campaigns/${clone.id}`);
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Could not duplicate campaign', { variant: 'error' });
        }
    };

    if (!hasPerm('campaign', 'list')) {
        return (
            <Box p={2}>
                <Typography>You do not have access to campaigns.</Typography>
            </Box>
        );
    }
    if (!userCanAccessCampaignsUi()) {
        return <Redirect to="/notifications" />;
    }
    if (!loaded) return null;
    if (notFound) {
        return (
            <Box sx={{ p: 2 }}>
                <PageMeta title="Campaign not found" />
                <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 2 }}>
                    Back to campaigns
                </Button>
                <Typography>This campaign could not be found.</Typography>
            </Box>
        );
    }
    if (!record) return null;

    const title = isNew ? 'Create New Campaign' : `Edit Campaign — ${record.name || 'Untitled'}`;
    // CP-8423 (Martina): [CANCEL] [SAVE DRAFT] sit in each editable step's bottom bar, not the header.
    // SAVE DRAFT is outlined so the step's own CONTINUE / SEND NOW stays the one filled button: a
    // filled Save draft beside a filled Send now invites sending by mistake. Cancel reuses goBack;
    // the <Prompt> still guards unsaved changes.
    const draftActions = readOnly ? null : (
        <FormActionButtons
            mode={isNew ? 'create' : 'edit'}
            size="medium"
            onCancel={goBack}
            onSubmit={onSave}
            submitDisabled={saving || !dirty}
            submitVariant="outlined"
            submitIcon={<SaveIcon />}
            submitLabel={isNew || record.status === CAMPAIGN_STATUS.DRAFT ? 'Save draft' : 'Save'}
        />
    );
    const statusInfo = STATUS_CHIP[record.status] || STATUS_CHIP[CAMPAIGN_STATUS.DRAFT];
    // Default destination for the "Send test" dialog — the signed-in
    // operator's own address, pulled from the bootstrap blob. Backend
    // falls back to request.user.email when we send an empty value, so
    // this is only a convenience pre-fill.
    let defaultTestEmail = '';
    try {
        const raw = window.localStorage.getItem('bootstrap');
        if (raw) defaultTestEmail = JSON.parse(raw)?.user?.email || '';
    } catch {
        defaultTestEmail = '';
    }
    // Send-readiness checks. Matches what the backend's
    // ``_validate_send_preconditions`` enforces — failing fast on the
    // dashboard so the operator doesn't see the Send dialog confirm and
    // then a 400. Currently: email enabled implies subject is required,
    // and a channel that would deliver the shared message body implies a
    // non-blank body (CP-8076).
    const emailRow = (record.delivery_methods || []).find(
        (r) => Number(r.method) === DELIVERY_METHOD.EMAIL,
    );
    const chatRow = (record.delivery_methods || []).find(
        (r) => Number(r.method) === DELIVERY_METHOD.CHAT,
    );
    const emailEnabled = !!(emailRow && emailRow.enabled !== false);
    const chatEnabled = !!(chatRow && chatRow.enabled !== false);
    const mobileRow = (record.delivery_methods || []).find(
        (r) => Number(r.method) === DELIVERY_METHOD.MOBILE_PHONE_NUMBER,
    );
    const chatOrMobileRow = (record.delivery_methods || []).find(
        (r) => Number(r.method) === DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER,
    );
    const airbnbRow = (record.delivery_methods || []).find(
        (r) => Number(r.method) === DELIVERY_METHOD.AIRBNB,
    );
    const mobileEnabled = !!(mobileRow && mobileRow.enabled !== false);
    const chatOrMobileEnabled = !!(chatOrMobileRow && chatOrMobileRow.enabled !== false);
    const airbnbEnabled = !!(airbnbRow && airbnbRow.enabled !== false);
    // Channel set is gated by purpose: Marketing shows the standalone
    // Chat + Mobile options (operator picks each independently), while
    // Transactional always includes in-app chat (banner, not a card) and
    // offers additive checkboxes — SMS/WhatsApp fallback (the composite
    // row when checked, standalone CHAT when unchecked), Airbnb, Email.
    // Purpose changes also clear the now-hidden delivery rows; see the
    // Purpose RadioGroup onChange handler.
    const isTransactional = (record.purpose || CAMPAIGN_PURPOSE.MARKETING) === CAMPAIGN_PURPOSE.TRANSACTIONAL;
    // Cancelled-booking policy. The stored flag is only honored for
    // Promotional; Essential always excludes cancelled bookings (hard rule,
    // enforced backend-side too). ``cancelledExcluded`` is what actually
    // happens and drives the visible audience banners.
    const includeCancelled = record.include_cancelled_bookings !== false;
    const cancelledExcluded = isTransactional || !includeCancelled;
    const emailOverrideSet = emailEnabled
        && !!(emailRow?.email_message ?? '').trim();
    const emailSubjectMissing = emailEnabled && !((emailRow?.email_subject ?? '').trim());
    // Computed once at the top so both the Audience and Message tabs can
    // use it without IIFE wrapping.
    const audienceMode = audienceModeForSendAs(record.send_as);

    // Variable availability notes: scan every authored source for
    // {{ tokens }} and flag what the chosen send mode can't fill. Rendered
    // above the body fields on Compose. Plain computation, not useMemo —
    // this sits below the loading early-returns, where hooks are illegal.
    const variableNotes = variableAvailabilityNotes(record.send_as || SEND_AS.PRIVATE_MESSAGE, [
        record.message_body,
        record.mobile_message_body,
        emailRow?.email_message,
        emailRow?.email_subject,
    ]);

    // ---- Required-field validation (CP-8076) ---------------------------
    //
    // Campaign name is always required. The shared message body is
    // required whenever at least one enabled channel would deliver it —
    // i.e. unless every enabled channel has its own version (mobile body
    // for SMS/WhatsApp, rich override for email; chat/Airbnb always use
    // the shared body). No channels selected yet also requires the body
    // (the backend defaults to chat when no delivery rows exist).
    const nameMissing = !(record.name || '').trim();
    const mobileVersionSet = !!(record.mobile_message_body || '').trim();
    const anyChannelEnabled = chatEnabled || mobileEnabled || chatOrMobileEnabled
        || emailEnabled || airbnbEnabled;
    const sharedBodyHasAudience = chatEnabled
        || chatOrMobileEnabled
        || airbnbEnabled
        || (mobileEnabled && !mobileVersionSet)
        || (emailEnabled && !emailOverrideSet);
    const messageBodyMissing = (!anyChannelEnabled || sharedBodyHasAudience)
        && !(record.message_body || '').trim();
    // Essential campaigns always post to in-app chat. Unchecking every
    // "Also deliver via" channel is allowed (CP-8143: chat-only
    // campaigns) — but guests without the app then get no off-thread
    // copy at all (campaign chat posts suppress the chat→SMS/WhatsApp
    // relay), so it's surfaced as a warning rather than a blocker.
    const essentialChatOnly = isTransactional
        && !chatOrMobileEnabled && !airbnbEnabled && !emailEnabled;
    const composeStepInvalid = nameMissing || messageBodyMissing
        || emailSubjectMissing;

    // Oversized-attachment warnings for the mobile channels: files past
    // Twilio's caps (5 MB MMS media total; 16 MB per WhatsApp file, 5 MB
    // per WhatsApp image) are delivered as links instead of native media,
    // so surface it while composing. Warn, don't block — everything still
    // delivers, just less prettily.
    const attachmentMobileWarnings = [];
    if (mobileEnabled || chatOrMobileEnabled) {
        const atts = record.attachments || [];
        const mmsMediaBytes = atts
            .filter((a) => MMS_MEDIA_EXTENSIONS.includes(attachmentExtension(a)))
            .reduce((sum, a) => sum + (a.size || 0), 0);
        if (mmsMediaBytes > MMS_TOTAL_MEDIA_BYTES) {
            attachmentMobileWarnings.push(
                `Your image attachments total ${formatMB(mmsMediaBytes)}, over the `
                + '5 MB MMS limit — images past the limit will reach SMS recipients '
                + 'as links instead of pictures. Remove or shrink images to get '
                + 'under 5 MB.',
            );
        }
        atts.filter((a) => WHATSAPP_MEDIA_EXTENSIONS.includes(attachmentExtension(a)))
            .forEach((a) => {
                const isImage = IMAGE_EXTENSIONS.includes(attachmentExtension(a));
                const cap = isImage ? WHATSAPP_IMAGE_BYTES : WHATSAPP_MEDIA_BYTES;
                if ((a.size || 0) > cap) {
                    attachmentMobileWarnings.push(
                        `"${a.name || `Attachment #${a.id}`}" is ${formatMB(a.size)}, over `
                        + `WhatsApp's ${isImage ? '5 MB image' : '16 MB media'} limit — `
                        + 'WhatsApp recipients will get it as a link instead.',
                    );
                }
            });
    }

    // Twilio's 1,600-character limit on the SMS/WhatsApp text (the mobile version when set, else the shared body):
    // over it, no guest gets the text, so Send is blocked like the backend's ``_check_mobile_length`` does.
    const mobileLength = (mobileEnabled || chatOrMobileEnabled)
        ? campaignMobileLength(mobileVersionSet ? record.mobile_message_body : record.message_body, {
            smsOptOutSuffix: filterOptions?.sms?.opt_out_suffix,
            whatsappOptOutSuffix: filterOptions?.sms?.whatsapp_opt_out_suffix,
            ...mobileAttachmentLinks(record.attachments),
        })
        : null;
    const mobileTooLong = !!mobileLength?.over;

    // "Send test" needs *some* testable content and write access, but works
    // from any status. A blank shared body is fine when the channel being
    // tested has its own content — the mobile version for phone tests
    // (CP-8018 mobile-only campaigns) or the rich override for email tests.
    // The backend send_test view applies the same per-channel gate.
    const canSendTest = hasWritePerm && (
        !!(record.message_body || '').trim()
        || mobileVersionSet
        || !!(emailRow?.email_message ?? '').trim()
    );

    // Audience-step validation: every filter row must carry the value its
    // operator needs (an incomplete row is silently ignored by the backend,
    // widening the audience). Column registry depends on the audience mode;
    // when the registry hasn't loaded yet we can't classify rows, so skip.
    const audienceFilterColumns = !filterOptions
        ? []
        : audienceMode === 'channel'
            ? (filterOptions.channel_columns || [])
            : audienceMode === 'main_office'
                ? (filterOptions.main_office_columns || [])
                : (filterOptions.columns || []);
    const activeAudienceFilters = audienceMode === 'channel'
        ? (record.audience_channel_filters || [])
        : (record.audience_filters || []);
    const audienceStepInvalid = audienceFilterColumns.length > 0
        && activeAudienceFilters.some((row) => isFilterRowIncomplete(row, audienceFilterColumns));

    const sendBlockedReason = messageBodyMissing
        ? 'Add a message body before sending.'
        : emailSubjectMissing
            ? 'Add an email subject before sending.'
            : mobileTooLong
                ? `The SMS/WhatsApp text is over ${MOBILE_MAX_CHARACTERS.toLocaleString('en-US')} characters, `
                    + 'so no guest would get it. Write a shorter mobile version before sending.'
                : audienceStepInvalid
                    ? 'Complete or remove the incomplete audience filters before sending.'
                    : null;

    // Step-navigation gate: mandatory fields block leaving their step in
    // any direction (stepper clicks included), per CP-8076. Read-only
    // campaigns (sent/ongoing) are exempt — there's nothing left to fix.
    const requestStep = (nextKey) => {
        if (nextKey === activeStep) return;
        if (!readOnly) {
            if (activeStep === STEP_COMPOSE && composeStepInvalid) {
                setShowComposeErrors(true);
                enqueueSnackbar('Fill in the required fields before leaving this step.', { variant: 'error' });
                return;
            }
            if (activeStep === STEP_AUDIENCE && audienceStepInvalid) {
                setShowAudienceErrors(true);
                enqueueSnackbar('Complete or remove the incomplete audience filters before leaving this step.', { variant: 'error' });
                return;
            }
        }
        setStep(nextKey);
    };
    // Channel labels for the Send dialog summary (item #4).
    const channelsEnabled = [
        chatEnabled ? 'In-app chat' : null,
        mobileEnabled ? 'Mobile (SMS/WhatsApp)' : null,
        chatOrMobileEnabled ? 'In-app chat, else SMS/WhatsApp' : null,
        emailEnabled ? 'Email' : null,
    ].filter(Boolean);
    // Backend cap-usage channel keys for the channels the campaign will
    // actually fire on. Keep aligned with the keys produced by
    // ``_get_cap_usage_for_operator`` server-side; the composite
    // methods expand to multiple underlying caps via
    // ``CAP_CHANNEL_KEYS_FOR_DELIVERY``.
    const deliveryChannelKeys = [
        chatEnabled ? 'chat' : null,
        emailEnabled ? 'email' : null,
        mobileEnabled ? 'mobile_phone_number' : null,
        chatOrMobileEnabled ? 'chat_if_available_or_mobile_phone_number' : null,
    ].filter(Boolean);

    return (
        <div className={classes.root}>
            <PageMeta title={title} />
            <Prompt
                when={dirty && !saving}
                message={(location) => {
                    if (location.pathname === history.location.pathname) return true;
                    return 'You have unsaved changes. Leave anyway?';
                }}
            />

            <div className={classes.header}>
                {/* Martina: back is just the small blue arrow, as on the notification editors (NavigateBackBar). */}
                <Tooltip title="Back">
                    <IconButton onClick={goBack} aria-label="Back" sx={{ p: 0, color: '#007AFF' }}>
                        <ChevronLeftIcon />
                    </IconButton>
                </Tooltip>
                <Typography className={classes.title} component="h1">
                    {title}
                </Typography>
                <Chip size="small" label={statusInfo.label} color={statusInfo.color} />
                {record.status === CAMPAIGN_STATUS.SCHEDULED && record.scheduled_at && (
                    <Typography variant="caption" color="text.secondary">
                        for {moment(record.scheduled_at).format('MMM D, YYYY, h:mm A')}{' '}
                        {operatorLocalTimeLabel()}
                    </Typography>
                )}
                {!isNew && (
                    <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={onDuplicate}
                        disabled={!hasWritePerm}
                    >
                        Duplicate
                    </Button>
                )}
                {!readOnlyByStatus && (
                    <Button
                        color="primary"
                        onClick={() => setTestDialogOpen(true)}
                        disabled={!canSendTest || saving || sendingTest}
                    >
                        Send test
                    </Button>
                )}
            </div>

            {record.status === CAMPAIGN_STATUS.ONGOING && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Alert severity="info">
                        Sending in progress&hellip; this page refreshes automatically when the
                        send finishes. Large audiences may take a few minutes.
                    </Alert>
                </Box>
            )}
            {readOnlyByStatus && record.status !== CAMPAIGN_STATUS.ONGOING && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Alert severity="info">
                        This campaign is {record.status} and can no longer be edited. Use "Duplicate" to make a variant.
                    </Alert>
                </Box>
            )}
            {record.status === CAMPAIGN_STATUS.FAILED && record.last_error && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Alert severity="error" icon={<ErrorOutlineIcon />}>
                        Last send failed: {record.last_error}
                    </Alert>
                </Box>
            )}

            <Box className={classes.stepperWrap} sx={{ mx: 'auto' }}>
                <Stepper nonLinear activeStep={visibleSteps.findIndex((s) => s.key === activeStep)}>
                    {visibleSteps.map((s) => (
                        <Step key={s.key} completed={false}>
                            <StepButton color="inherit" onClick={() => requestStep(s.key)}>
                                {s.label}
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Box sx={{ px: 2, pt: 2 }} className={classes.stepBody}>
                    {activeStep === STEP_COMPOSE && (
                        <>
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            <CardContent>
                                <Typography className={classes.cardTitle}>Campaign basics</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label="Campaign name"
                                            value={record.name}
                                            onChange={(e) => update({ name: e.target.value })}
                                            variant="filled"
                                            fullWidth
                                            disabled={readOnly}
                                            required
                                            error={showComposeErrors && nameMissing}
                                            helperText={
                                                showComposeErrors && nameMissing
                                                    ? 'Campaign name is required.'
                                                    : ''
                                            }
                                        />
                                        <Box>
                                        {/* Purpose as two click-to-select tiles
                                            (Promotional = marketing, Essential =
                                            transactional). Mirrors the mockup; the
                                            underlying value + channel-gating is unchanged
                                            (see ``applyPurpose``). */}
                                        <FormLabel
                                            component="legend"
                                            sx={{ mb: 1, '&.Mui-focused': { color: 'text.primary' } }}
                                        >
                                            Purpose
                                        </FormLabel>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                            <SelectableCard
                                                selected={!isTransactional}
                                                disabled={readOnly || !promotionalEnabled}
                                                onToggle={() => {
                                                    if (!promotionalEnabled) return;
                                                    applyPurpose(CAMPAIGN_PURPOSE.MARKETING);
                                                }}
                                                control={<Radio checked={!isTransactional} disabled={readOnly || !promotionalEnabled} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                                                title="Promotional"
                                                description={promotionalEnabled
                                                    ? 'Offers & marketing. Skips guests who opted out of marketing.'
                                                    : 'Not available for your account yet.'}
                                            />
                                            <SelectableCard
                                                selected={isTransactional}
                                                disabled={readOnly}
                                                onToggle={() => applyPurpose(CAMPAIGN_PURPOSE.TRANSACTIONAL)}
                                                control={<Radio checked={isTransactional} disabled={readOnly} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                                                title="Essential"
                                                description="Critical info. Reaches every guest, including those who opted out of marketing."
                                            />
                                        </Box>
                                        {!isTransactional && promotionalEnabled && (
                                            // Backend rollout phase 4: Promotional WhatsApp is a normal message for guests inside
                                            // their 24-hour window, else WhatsApp's marketing template
                                            // (``campaign_promotional.whatsapp_room``: ~770 characters for most operators; the
                                            // send refuses a longer message).
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                On WhatsApp, guests who messaged you in the last 24 hours get this as written. Everyone
                                                else gets it in WhatsApp&apos;s marketing template, on one line (line breaks become
                                                &ldquo;&nbsp;·&nbsp;&rdquo;), with attachments as links, up to about 750 characters.
                                            </Typography>
                                        )}
                                        </Box>
                                        {/* Cancelled-booking control, right under
                                            Purpose. Essential never reaches cancelled
                                            bookings (hard rule, shown locked); Promotional
                                            includes them by default and can opt out. The
                                            effect is mirrored as a banner on the Audience
                                            step + Send review so it's never hidden. */}
                                        <Box>
                                            <FormLabel
                                                component="legend"
                                                sx={{ mb: 1, '&.Mui-focused': { color: 'text.primary' } }}
                                            >
                                                Cancelled bookings
                                            </FormLabel>
                                            {isTransactional ? (
                                                <Alert severity="info" sx={{ py: 0.5 }}>
                                                    Essential messages are never sent to guests with a
                                                    cancelled booking.
                                                </Alert>
                                            ) : (
                                                <FormControlLabel
                                                    sx={{ alignItems: 'flex-start', m: 0 }}
                                                    control={
                                                        <Switch
                                                            checked={includeCancelled}
                                                            onChange={(e) =>
                                                                update({ include_cancelled_bookings: e.target.checked })
                                                            }
                                                            disabled={readOnly}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ mt: 0.75 }}>
                                                            <Typography variant="body2">
                                                                Also send to guests who cancelled their booking
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {includeCancelled
                                                                    ? 'Guests with a cancelled booking are included in the audience.'
                                                                    : 'Guests with a cancelled booking are excluded from the audience.'}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            )}
                                        </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* "How it's sent" — channel picker as cards. Reuses the
                            same ``delivery_methods`` plumbing as before; the set of
                            cards shown is purpose-gated. Promotional: standalone
                            Chat + Mobile cards. Essential: chat is always included
                            (banner, not a card) and the checkboxes below pick the
                            additional channels; the SMS/WhatsApp checkbox swaps
                            between the composite row (checked — chat + mobile
                            fallback) and the standalone CHAT row (unchecked — chat
                            only from this pair), so the "chat always posts"
                            guarantee survives either way without backend changes.
                            Email is shown for both purposes. */}
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            <CardContent>
                                <Typography className={classes.cardTitle}>How it&rsquo;s sent</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {!isTransactional && (
                                        <ChannelCard
                                            methodId={DELIVERY_METHOD.CHAT}
                                            title="In-app Chat"
                                            description="Sent to the guest's app."
                                            deliveryMethods={record.delivery_methods || []}
                                            onChange={(rows) => update({ delivery_methods: rows })}
                                            disabled={readOnly}
                                        />
                                    )}
                                    {!isTransactional && (
                                        <ChannelCard
                                            methodId={DELIVERY_METHOD.MOBILE_PHONE_NUMBER}
                                            title="Mobile"
                                            description="SMS for +1 numbers, WhatsApp for international numbers."
                                            deliveryMethods={record.delivery_methods || []}
                                            onChange={(rows) => update({ delivery_methods: rows })}
                                            disabled={readOnly}
                                        />
                                    )}
                                    {isTransactional && (
                                        <Alert severity="info" icon={<ChatBubbleOutlineIcon fontSize="small" />}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                In-app Chat is always included
                                            </Typography>
                                            <Typography variant="body2">
                                                Every message is posted to the guest&rsquo;s tour chat thread.
                                                Airbnb and Email are always sent in addition &mdash; but
                                                SMS/WhatsApp is a fallback only, and won&rsquo;t be sent to
                                                guests who have the app installed with notifications on.
                                            </Typography>
                                        </Alert>
                                    )}
                                    {isTransactional && (
                                        <Typography
                                            variant="overline"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.5, mt: 0.5 }}
                                        >
                                            Also deliver via
                                        </Typography>
                                    )}
                                    {isTransactional && (
                                        <SelectableCard
                                            selected={chatOrMobileEnabled}
                                            disabled={readOnly}
                                            onToggle={(next) => {
                                                // Swap which chat-carrying row is enabled so
                                                // in-app chat always posts: checked → composite
                                                // (chat + SMS/WhatsApp fallback); unchecked →
                                                // standalone CHAT. Never both (CP-8084).
                                                let rows = record.delivery_methods || [];
                                                rows = withDeliveryEnabled(
                                                    rows,
                                                    DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER,
                                                    next,
                                                );
                                                rows = withDeliveryEnabled(rows, DELIVERY_METHOD.CHAT, !next);
                                                update({ delivery_methods: rows });
                                            }}
                                            control={(
                                                <Checkbox
                                                    checked={chatOrMobileEnabled}
                                                    disabled={readOnly}
                                                    tabIndex={-1}
                                                    sx={{ p: 0, mt: 0.25 }}
                                                />
                                            )}
                                            title="SMS / WhatsApp"
                                            description="SMS for +1 numbers, WhatsApp for international numbers. Only sent to guests who don't have the app installed with notifications on — guests who do get in-app chat only."
                                        />
                                    )}
                                    {isTransactional && !chatOrMobileEnabled && !essentialChatOnly && (
                                        <Alert severity="warning">
                                            Without the SMS/WhatsApp fallback, guests who don&rsquo;t have the
                                            app won&rsquo;t receive a text &mdash; the message will wait in their
                                            chat thread until they open the app. Make sure your audience is
                                            reachable via the channels you&rsquo;ve selected.
                                        </Alert>
                                    )}
                                    {essentialChatOnly && (
                                        <Alert severity="warning">
                                            This campaign will be delivered to in-app chat only. Guests
                                            who don&rsquo;t have the app installed won&rsquo;t receive a text
                                            or email &mdash; the message will wait in their chat thread until
                                            they open the app.
                                        </Alert>
                                    )}
                                    {/* CP-8084: "In-app Chat, else SMS/WhatsApp" already
                                        delivers in-app chat to app users, so also enabling
                                        the standalone "In-app Chat" channel is redundant —
                                        guests WITHOUT the app then get two messages (chat +
                                        SMS/WhatsApp). App users are unaffected (the chat is
                                        de-duped per conversation server-side). This state is
                                        usually a leftover from switching purpose or
                                        duplicating a campaign, and one of the two cards is
                                        hidden by the purpose gate — so the one-click fix
                                        removes whichever chat channel isn't part of the
                                        current purpose. */}
                                    {chatEnabled && chatOrMobileEnabled && (
                                        <Alert
                                            severity="warning"
                                            action={!readOnly && (
                                                <Button
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => update({
                                                        delivery_methods: withDeliveryEnabled(
                                                            record.delivery_methods || [],
                                                            isTransactional
                                                                ? DELIVERY_METHOD.CHAT
                                                                : DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER,
                                                            false,
                                                        ),
                                                    })}
                                                >
                                                    Remove duplicate
                                                </Button>
                                            )}
                                        >
                                            <strong>&ldquo;In-app Chat, else SMS/WhatsApp&rdquo; already covers in-app chat.</strong>{' '}
                                            With <strong>In-app Chat</strong> also enabled, guests without the app receive
                                            two messages — an in-app chat <em>and</em> an SMS/WhatsApp. Remove one to
                                            avoid duplicate sends.
                                        </Alert>
                                    )}
                                    {/* Same overlap on the mobile side: the composite's
                                        SMS/WhatsApp leg + standalone Mobile both text the
                                        same no-app guests — two billed messages each.
                                        Unreachable via the purpose-gated picker (one card
                                        is always hidden) but real for legacy/API-written
                                        rows, which duplication propagates. */}
                                    {mobileEnabled && chatOrMobileEnabled && (
                                        <Alert
                                            severity="warning"
                                            action={!readOnly && (
                                                <Button
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => update({
                                                        delivery_methods: withDeliveryEnabled(
                                                            record.delivery_methods || [],
                                                            isTransactional
                                                                ? DELIVERY_METHOD.MOBILE_PHONE_NUMBER
                                                                : DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER,
                                                            false,
                                                        ),
                                                    })}
                                                >
                                                    Remove duplicate
                                                </Button>
                                            )}
                                        >
                                            <strong>&ldquo;In-app Chat, else SMS/WhatsApp&rdquo; already covers SMS/WhatsApp.</strong>{' '}
                                            With <strong>Mobile</strong> also enabled, guests without the app receive — and
                                            you are billed for — two text messages. Remove one to avoid duplicate sends.
                                        </Alert>
                                    )}
                                    {/* Airbnb is its own explicit channel — used
                                        to auto-fan from In-app Chat for Airbnb-
                                        source guests, but that implicit rule was
                                        confusing ("what does 'also reaches
                                        Airbnb' actually mean?"). Now: check the
                                        box to reach Airbnb-source guests via
                                        their Airbnb thread. Non-Airbnb-source
                                        recipients are skipped server-side with
                                        no error. */}
                                    <ChannelCard
                                        methodId={DELIVERY_METHOD.AIRBNB}
                                        title="Airbnb"
                                        description="Delivered to the recipient's Airbnb Experiences thread. Only reaches guests booked via Airbnb."
                                        deliveryMethods={record.delivery_methods || []}
                                        onChange={(rows) => update({ delivery_methods: rows })}
                                        disabled={readOnly}
                                    />
                                    <ChannelCard
                                        methodId={DELIVERY_METHOD.EMAIL}
                                        title="Email"
                                        description="A full email with a subject line and rich formatting."
                                        deliveryMethods={record.delivery_methods || []}
                                        onChange={(rows) => update({ delivery_methods: rows })}
                                        disabled={readOnly}
                                    />
                                    <EmailSubjectField
                                        deliveryMethods={record.delivery_methods || []}
                                        onChange={(rows) => update({ delivery_methods: rows })}
                                        disabled={readOnly}
                                        forceShowError={showComposeErrors}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                        </>
                    )}

                    {activeStep === STEP_AUDIENCE && (
                        <>
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            <CardContent>
                                <Typography className={classes.cardTitle}>Where should we send it?</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                    <SelectableCard
                                        selected={(record.send_as || SEND_AS.PRIVATE_MESSAGE) === SEND_AS.PRIVATE_MESSAGE}
                                        disabled={readOnly}
                                        onToggle={() => update({ send_as: SEND_AS.PRIVATE_MESSAGE })}
                                        control={<Radio checked={(record.send_as || SEND_AS.PRIVATE_MESSAGE) === SEND_AS.PRIVATE_MESSAGE} disabled={readOnly} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                                        title="Per-trip chat"
                                        description="Posts in each guest's tour chat thread."
                                    />
                                    <SelectableCard
                                        selected={record.send_as === SEND_AS.MAIN_OFFICE}
                                        disabled={readOnly}
                                        onToggle={() => update({ send_as: SEND_AS.MAIN_OFFICE })}
                                        control={<Radio checked={record.send_as === SEND_AS.MAIN_OFFICE} disabled={readOnly} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                                        title="Main office chat"
                                        description="Posts in each guest's main office chat thread."
                                    />
                                    {(SHOW_ANNOUNCEMENT_SEND_AS || record.send_as === SEND_AS.ANNOUNCEMENT) && (
                                        <SelectableCard
                                            selected={record.send_as === SEND_AS.ANNOUNCEMENT}
                                            disabled={readOnly}
                                            onToggle={() => update({ send_as: SEND_AS.ANNOUNCEMENT })}
                                            control={<Radio checked={record.send_as === SEND_AS.ANNOUNCEMENT} disabled={readOnly} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />}
                                            title="Announcement"
                                            description="Posts one message in each matching tour announcements thread."
                                        />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            <CardContent>
                                <Typography className={classes.cardTitle}>Audience</Typography>
                                {audienceMode === 'channel' ? (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Announcement mode targets <strong>chat channels</strong>, not individual
                                        guests. The campaign posts exactly one message into each matching
                                        group-tour announcements thread. The optional ruleset is ignored in
                                        this mode (rulesets evaluate per user, but everyone in a channel sees
                                        an announcement either way).
                                    </Alert>
                                ) : (
                                    <>
                                        {audienceMode === 'main_office' && (
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                Main Office mode targets each recipient&rsquo;s personal Main
                                                Office thread (one post per user, regardless of how many tours
                                                they&rsquo;ve booked). The audience is sourced from existing
                                                live MO threads only &mdash; users who don&rsquo;t already have
                                                one are not eligible, since the campaign send never auto-creates
                                                threads.
                                            </Alert>
                                        )}
                                    </>
                                )}
                                {audienceMode === 'channel' ? (
                                    <CampaignAudienceBuilder
                                        key="audience-channel"
                                        campaignId={record.id}
                                        mode="channel"
                                        filters={record.audience_channel_filters || []}
                                        onFiltersChange={(f) => update({ audience_channel_filters: f })}
                                        filterRegistry={
                                            filterOptions
                                                ? { ...filterOptions, columns: filterOptions.channel_columns || [] }
                                                : null
                                        }
                                        disabled={readOnly}
                                        onAudienceCount={setAudienceCount}
                                        showValidation={showAudienceErrors}
                                    />
                                ) : audienceMode === 'main_office' ? (
                                    <CampaignAudienceBuilder
                                        key="audience-main-office"
                                        campaignId={record.id}
                                        mode="main_office"
                                        filters={record.audience_filters || []}
                                        onFiltersChange={(f) => update({ audience_filters: f })}
                                        filterRegistry={
                                            filterOptions
                                                ? { ...filterOptions, columns: filterOptions.main_office_columns || [] }
                                                : null
                                        }
                                        disabled={readOnly}
                                        onAudienceCount={setAudienceCount}
                                        showValidation={showAudienceErrors}
                                    />
                                ) : (
                                    <>
                                    {/* Effective cancellation rule, made explicit
                                        in the audience surface itself (no hidden
                                        logic). The preview count below already
                                        reflects it. */}
                                    <Alert
                                        severity={cancelledExcluded ? 'info' : 'warning'}
                                        sx={{ mb: 2 }}
                                    >
                                        {cancelledExcluded ? (
                                            <>
                                                Guests with a <strong>cancelled booking are excluded</strong> from
                                                this audience
                                                {isTransactional
                                                    ? ' (Essential campaigns never send to cancelled bookings).'
                                                    : ' — turn on “Also send to guests who cancelled their booking” under Purpose to include them.'}
                                            </>
                                        ) : (
                                            <>
                                                Guests with a <strong>cancelled booking are included</strong> in this
                                                audience. Turn off “Also send to guests who cancelled their booking”
                                                under Purpose to exclude them.
                                            </>
                                        )}
                                    </Alert>
                                    <CampaignAudienceBuilder
                                        key="audience-user"
                                        campaignId={record.id}
                                        mode="user"
                                        filters={record.audience_filters || []}
                                        onFiltersChange={(f) => update({ audience_filters: f })}
                                        filterRegistry={filterOptions}
                                        disabled={readOnly}
                                        purpose={record.purpose}
                                        includeCancelledBookings={record.include_cancelled_bookings !== false}
                                        // Only require a chat conversation when chat is the
                                        // *only* way to reach the audience — i.e. no email +
                                        // no Mobile + no Chat-or-Mobile. Mobile reaches anyone
                                        // with a phone (no conversation needed); email reaches
                                        // anyone with an email; Chat-or-Mobile falls through
                                        // to SMS/WhatsApp for users without the app. Filtering
                                        // out non-chat users when any of those is on would
                                        // wrongly exclude reachable recipients from the preview.
                                        requireConversation={
                                            chatEnabled
                                            && !emailEnabled
                                            && !mobileEnabled
                                            && !chatOrMobileEnabled
                                        }
                                        onAudienceCount={setAudienceCount}
                                        showValidation={showAudienceErrors}
                                    />
                                    </>
                                )}
                                {/* Advanced expander sits below the audience
                                    builder + preview so the primary filter
                                    flow is uninterrupted. Hidden entirely
                                    for channel/announcement mode — rulesets
                                    are user-scoped and don't apply when the
                                    target is a chat channel. Also hidden by
                                    the ``SHOW_ADVANCED_RULESET`` launch flag
                                    (operators found it confusing), except for
                                    campaigns that already have a ruleset set
                                    so it stays viewable/clearable. */}
                                {audienceMode !== 'channel'
                                    && (SHOW_ADVANCED_RULESET || !!record.ruleset) && (
                                    <Box sx={{ mt: 2 }}>
                                        <AdvancedRulesetSection
                                            record={record}
                                            update={update}
                                            readOnly={readOnly}
                                        />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                        <Box className={classes.stepActions}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => requestStep(STEP_COMPOSE)}
                            >
                                Back
                            </Button>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
                                {draftActions}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<ChevronRightIcon />}
                                    onClick={() => requestStep(STEP_SEND)}
                                >
                                    Continue
                                </Button>
                            </Box>
                        </Box>
                        </>
                    )}

                    {/* Message authoring stays on the Compose step alongside
                        the channel picker — purpose + channel choices drive the
                        audience preview's reachability filters, so co-locating
                        them keeps cause-and-effect in one place. */}
                    {activeStep === STEP_COMPOSE && (
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            <CardContent>
                                <Typography className={classes.cardTitle}>Message</Typography>
                                {variableNotes.map((note) => (
                                    <Alert key={note.text} severity={note.severity} sx={{ mb: 1.5 }}>
                                        {note.text}
                                    </Alert>
                                ))}
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Box>
                                        <Box sx={{ mb: 1.5 }}>
                                            <VariableInsertBar
                                                variablesTree={CAMPAIGN_VARIABLES_TREE}
                                                onInsert={insertIntoMessageBody}
                                                disabled={readOnly}
                                            />
                                        </Box>
                                        <TextField
                                            label="Message body"
                                            value={record.message_body}
                                            onChange={(e) => update({ message_body: e.target.value })}
                                            variant="filled"
                                            fullWidth
                                            multiline
                                            minRows={6}
                                            disabled={readOnly}
                                            inputRef={messageBodyRef}
                                            // The asterisk drops only in the edge case where
                                            // every enabled channel has its own version and
                                            // the shared body is genuinely unused.
                                            required={!anyChannelEnabled || sharedBodyHasAudience}
                                            error={showComposeErrors && messageBodyMissing}
                                            helperText={(() => {
                                                if (showComposeErrors && messageBodyMissing) {
                                                    return 'Message body is required.';
                                                }
                                                // Body recipients = chat (if a chat-capable
                                                // channel is on) + SMS/WhatsApp (unless the
                                                // mobile version below replaces it) + Airbnb +
                                                // email (if email on AND no override set;
                                                // override replaces the body for email).
                                                // ``mobileVersionSet`` is the component-scope
                                                // const — same derivation as the validation.
                                                const anyMobile = mobileEnabled || chatOrMobileEnabled;
                                                const sentTo = [];
                                                if (chatEnabled || chatOrMobileEnabled) sentTo.push('chat');
                                                if (anyMobile && !mobileVersionSet) sentTo.push('SMS/WhatsApp');
                                                if (airbnbEnabled) sentTo.push('Airbnb');
                                                if (emailEnabled && !emailOverrideSet) sentTo.push('email');
                                                let prefix;
                                                if (sentTo.length > 0) {
                                                    prefix = `Sent to ${sentTo.join(' and ')} recipients.`;
                                                } else if (!anyChannelEnabled) {
                                                    prefix = 'Enable a channel above to send.';
                                                } else {
                                                    // Every enabled channel has its own
                                                    // version → this body has no audience.
                                                    prefix = 'Not sent — recipients see the channel-specific versions below.';
                                                }
                                                // Cross-reference the per-channel overrides so
                                                // the operator knows recipients are split
                                                // between surfaces.
                                                const overrideNotes = [];
                                                if (sentTo.length > 0 && anyMobile && mobileVersionSet) {
                                                    overrideNotes.push(' SMS/WhatsApp recipients will get the mobile version below instead.');
                                                }
                                                if (sentTo.length > 0 && emailEnabled && emailOverrideSet) {
                                                    overrideNotes.push(' Email recipients will see the override below instead of this body.');
                                                }
                                                return `${prefix}${overrideNotes.join('')} Use Insert variable to personalize per recipient.`;
                                            })()}
                                        />
                                        {/* SMS recipients get this body unless the mobile
                                            version below is set — count it while it's the
                                            one being texted. */}
                                        {(mobileEnabled || chatOrMobileEnabled) && !mobileVersionSet && (
                                            <>
                                                <SmsSegmentCounter
                                                    text={record.message_body}
                                                    sms={filterOptions?.sms}
                                                />
                                                <MobileLengthNotice
                                                    length={mobileLength}
                                                    translated={!!record.translate_to_recipient_lang}
                                                    fix={'Write a shorter version in “Mobile version (SMS/WhatsApp)” '
                                                        + 'below; email and chat keep this message.'}
                                                />
                                            </>
                                        )}
                                    </Box>
                                    {/* Second content box (CP-8018): optional shorter
                                        variant for SMS/WhatsApp. Only shown when a
                                        mobile-capable channel is enabled — the field
                                        does nothing otherwise. Blank = mobile
                                        recipients get the main body above. */}
                                    {(mobileEnabled || chatOrMobileEnabled) && (
                                        <Box>
                                            <Box sx={{ mb: 1.5 }}>
                                                <VariableInsertBar
                                                    variablesTree={CAMPAIGN_VARIABLES_TREE}
                                                    onInsert={insertIntoMobileMessageBody}
                                                    disabled={readOnly}
                                                />
                                            </Box>
                                            <TextField
                                                label="Mobile version (SMS/WhatsApp) — optional"
                                                value={record.mobile_message_body || ''}
                                                onChange={(e) => update({ mobile_message_body: e.target.value })}
                                                variant="filled"
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                disabled={readOnly}
                                                inputRef={mobileMessageBodyRef}
                                                helperText={
                                                    (record.mobile_message_body || '').trim()
                                                        ? 'Sent to SMS/WhatsApp recipients instead of the message body above. Keep it short — SMS is billed per segment.'
                                                        : 'Optional shorter version for SMS/WhatsApp recipients. Leave blank to send the message body above.'
                                                }
                                            />
                                            <SmsSegmentCounter
                                                text={record.mobile_message_body}
                                                sms={filterOptions?.sms}
                                            />
                                            {mobileVersionSet && (
                                                <MobileLengthNotice
                                                    length={mobileLength}
                                                    translated={!!record.translate_to_recipient_lang}
                                                    fix="Shorten the mobile version."
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {/* Translate switch sits directly under the body fields
                                        because the only things it touches are the body, the
                                        mobile version and the email subject — *not* the rich
                                        email override below. By physically grouping it with the
                                        bodies (and away from the email customizer), the scope of
                                        "what gets translated" reads correctly at a glance.
                                        Helper caption + the inline warning below the email block
                                        reinforce the rule. */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!record.translate_to_recipient_lang}
                                                    onChange={(e) => update({
                                                        translate_to_recipient_lang: e.target.checked,
                                                    })}
                                                    disabled={readOnly}
                                                />
                                            }
                                            label="Translate to recipient's language (AI)"
                                            sx={{ mr: 0 }}
                                        />
                                        {/* Preview button only meaningful with a saved campaign id +
                                            translation toggle on. Saving is required because the
                                            preview endpoint reads ``campaign.message_body`` from the
                                            DB; an unsaved body wouldn't translate what the operator
                                            actually wrote. */}
                                        <Tooltip
                                            title={
                                                !record.id
                                                    ? 'Save the campaign before previewing translations'
                                                    : (!record.translate_to_recipient_lang
                                                        ? 'Turn on translation to preview'
                                                        : (dirty
                                                            ? 'Save unsaved changes to preview the latest body'
                                                            : ''))
                                            }
                                            disableHoverListener={
                                                !!record.id && !!record.translate_to_recipient_lang && !dirty
                                            }
                                        >
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setTranslationPreviewOpen(true)}
                                                    disabled={
                                                        !record.id
                                                        || !record.translate_to_recipient_lang
                                                        || dirty
                                                    }
                                                >
                                                    Preview translations
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Applies to the message body, mobile version and email
                                        subject. The rich email override below is sent
                                        as-authored regardless.
                                    </Typography>
                                    {/* Override expander lives below body + translate so the
                                        operator authors the shared body first, then optionally
                                        overrides for email. The Email Subject sits at the top
                                        with the channel checkbox instead. Renders only when the
                                        Email checkbox up top is checked. */}
                                    <EmailOverrideExpander
                                        deliveryMethods={record.delivery_methods || []}
                                        onChange={(rows) => update({ delivery_methods: rows })}
                                        disabled={readOnly}
                                        onOpenEmailPreview={() => setEmailPreviewOpen(true)}
                                        emailPreviewDisabledReason={
                                            !record.id
                                                ? 'Save the campaign before previewing email'
                                                : (dirty
                                                    ? 'Save unsaved changes to preview the latest email'
                                                    : '')
                                        }
                                    />
                                    {/* Warning sits adjacent to the override it's warning about,
                                        not next to the translate switch — operator's eye is on the
                                        override section when this becomes relevant. AI translation
                                        only touches the *shared* message body + email subject;
                                        per-method HTML overrides are sent verbatim because LLM
                                        translation of HTML routinely breaks formatting / inline
                                        tags / links. */}
                                    {!!record.translate_to_recipient_lang
                                        && emailEnabled
                                        && !!(emailRow?.email_message ?? '').trim() && (
                                        <Alert severity="warning">
                                            Your email override won&rsquo;t be translated &mdash;
                                            every email recipient will receive it in the language
                                            you wrote it. Clear the override to send the translated
                                            shared message body to email recipients instead.
                                        </Alert>
                                    )}
                                    {/* Attachments live at the bottom of the Message tab —
                                        below body + channels + translate — because they're
                                        the last optional layer the operator considers, and
                                        the helper text needs both channels' enabled state
                                        to describe what'll actually happen at send time. */}
                                    <AttachmentImagePicker
                                        label="Attachments (optional)"
                                        helperText={(() => {
                                            // One sentence per enabled channel — each carries
                                            // attachments its own way (mirrors the backend's
                                            // per-channel media gating). The composite counts
                                            // as both chat and SMS/WhatsApp.
                                            const parts = [];
                                            if (chatEnabled || chatOrMobileEnabled) {
                                                parts.push('Chat: each attachment is sent as its own media message after the body (each is a separately billable chat send).');
                                            }
                                            if (mobileEnabled || chatOrMobileEnabled) {
                                                parts.push('SMS: images are sent as MMS media with the message; other files as short links in the text.');
                                                parts.push('WhatsApp: images, documents (PDF/Office) and MP4 video are sent as media messages after the body; other files as short links.');
                                            }
                                            if (emailEnabled) {
                                                parts.push('Email: attachments appear as download links at the bottom of the body.');
                                            }
                                            if (airbnbEnabled) {
                                                parts.push('Airbnb: attachments are not sent (Airbnb messages are text-only).');
                                            }
                                            return parts.length
                                                ? parts.join(' ')
                                                : 'Enable a channel above to send attachments.';
                                        })()}
                                        value={record.attachments || []}
                                        onChange={(next) => update({ attachments: next })}
                                        disabled={readOnly}
                                    />
                                    {attachmentMobileWarnings.map((warning) => (
                                        <Alert key={warning} severity="warning" sx={{ mt: 1 }}>
                                            {warning}
                                        </Alert>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {activeStep === STEP_COMPOSE && (
                        <Box className={classes.stepActions} sx={{ justifyContent: 'flex-end' }}>
                            {draftActions}
                            <Button
                                variant="contained"
                                color="primary"
                                endIcon={<ChevronRightIcon />}
                                onClick={() => requestStep(STEP_AUDIENCE)}
                            >
                                Continue
                            </Button>
                        </Box>
                    )}

                    {activeStep === STEP_SEND && (
                        showAnalytics ? (
                            // Already sent (or sending): the Send step becomes a
                            // read-only recap. The results report lives on its own
                            // "Campaign Analytics" step now — surface a clear jump.
                            <>
                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Typography className={classes.cardTitle}>Preview</Typography>
                                    <MessagePreview
                                        record={record}
                                        chatEnabled={chatEnabled}
                                        mobileEnabled={mobileEnabled}
                                        chatOrMobileEnabled={chatOrMobileEnabled}
                                        emailEnabled={emailEnabled}
                                        emailRow={emailRow}
                                        onSendTest={() => setTestDialogOpen(true)}
                                        canSendTest={canSendTest}
                                    />
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Typography className={classes.cardTitle}>Sent</Typography>
                                    <Box sx={{ p: 1.5, bgcolor: '#F5F7FB', borderRadius: 1 }}>
                                        <Typography variant="body2">
                                            {record.status === CAMPAIGN_STATUS.ONGOING
                                                ? 'Sending in progress…'
                                                : record.sent_at
                                                    ? `Sent ${moment(record.sent_at).format('MMM D, YYYY, h:mm A')}`
                                                    : 'Sent'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {channelsEnabled.length === 0
                                                ? 'No channels selected'
                                                : `via ${channelsEnabled.join(', ')}`}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                            <Box className={classes.stepActions} sx={{ justifyContent: 'space-between' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => requestStep(STEP_AUDIENCE)}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<ChevronRightIcon />}
                                    onClick={() => requestStep(STEP_ANALYTICS)}
                                >
                                    View Campaign Analytics
                                </Button>
                            </Box>
                            </>
                        ) : (
                            <>
                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Typography className={classes.cardTitle}>Preview</Typography>
                                    <MessagePreview
                                        record={record}
                                        chatEnabled={chatEnabled}
                                        mobileEnabled={mobileEnabled}
                                        chatOrMobileEnabled={chatOrMobileEnabled}
                                        emailEnabled={emailEnabled}
                                        emailRow={emailRow}
                                        onSendTest={() => setTestDialogOpen(true)}
                                        canSendTest={canSendTest}
                                    />
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Typography className={classes.cardTitle}>Audience</Typography>
                                    <Box sx={{ p: 1.5, bgcolor: '#F5F7FB', borderRadius: 1 }}>
                                        <Typography variant="body2">
                                            <strong>
                                                {audienceCount == null ? '—' : Number(audienceCount).toLocaleString()}
                                            </strong>
                                            {' recipients'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {channelsEnabled.length === 0
                                                ? 'No channels selected'
                                                : `via ${channelsEnabled.join(', ')}`}
                                        </Typography>
                                        {record.send_as === SEND_AS.PRIVATE_MESSAGE && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {cancelledExcluded
                                                    ? 'Cancelled bookings: excluded'
                                                    : 'Cancelled bookings: included'}
                                            </Typography>
                                        )}
                                    </Box>
                                    {!hasPersistentRecord && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            Save the campaign before sending.
                                        </Alert>
                                    )}
                                    {hasPersistentRecord && dirty && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            You have unsaved changes — save the campaign to enable sending.
                                        </Alert>
                                    )}
                                    {sendBlockedReason && (
                                        <Alert severity="error" sx={{ mt: 2 }}>{sendBlockedReason}</Alert>
                                    )}
                                </CardContent>
                            </Card>
                            {readOnlyByStatus ? (
                                <Box className={classes.stepActions}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => requestStep(STEP_AUDIENCE)}
                                    >
                                        Back
                                    </Button>
                                </Box>
                            ) : (
                                <SendStepPanel
                                    draftActions={draftActions}
                                    onConfirm={doSendFlow}
                                    onCancelSchedule={
                                        record.status === CAMPAIGN_STATUS.SCHEDULED
                                            ? () => setCancelScheduleOpen(true)
                                            : undefined
                                    }
                                    onBack={() => requestStep(STEP_AUDIENCE)}
                                    saving={saving}
                                    initialScheduledAt={record.scheduled_at}
                                    initialMode={
                                        record.status === CAMPAIGN_STATUS.SCHEDULED
                                            ? CAMPAIGN_TIMING.SCHEDULED
                                            : CAMPAIGN_TIMING.NOW
                                    }
                                    recipientCount={audienceCount}
                                    deliveryChannelKeys={deliveryChannelKeys}
                                    campaignId={record.id}
                                    dataAPI={dataAPI}
                                    confirmDisabled={
                                        dirty || !hasPersistentRecord || !!sendBlockedReason
                                    }
                                    confirmDisabledReason={
                                        !hasPersistentRecord
                                            ? 'Save the campaign before sending.'
                                            : dirty
                                                ? 'Save your changes before sending.'
                                                : sendBlockedReason || ''
                                    }
                                    promotional={!isTransactional}
                                />
                            )}
                            </>
                        )
                    )}

                    {activeStep === STEP_ANALYTICS && showAnalytics && (
                        <>
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            <CardContent>
                                <Typography className={classes.cardTitle}>Campaign Analytics</Typography>
                                <CampaignReport campaignId={record.id} />
                            </CardContent>
                        </Card>
                        <Box className={classes.stepActions}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => requestStep(STEP_SEND)}
                            >
                                Back
                            </Button>
                        </Box>
                        </>
                    )}
            </Box>

            <SendTestDialog
                open={testDialogOpen}
                onClose={() => setTestDialogOpen(false)}
                defaultEmail={defaultTestEmail}
                onSend={doSendTest}
                sending={sendingTest}
                mobileVersionSet={mobileVersionSet}
            />
            <Dialog
                open={cancelScheduleOpen}
                onClose={() => setCancelScheduleOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Cancel scheduled send?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This campaign will be moved back to <strong>Draft</strong> and won&rsquo;t
                        send at its scheduled time. You can edit it and schedule it again later.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelScheduleOpen(false)} disabled={saving}>
                        Keep schedule
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={doCancelSchedule}
                        disabled={saving}
                    >
                        Cancel scheduled send
                    </Button>
                </DialogActions>
            </Dialog>
            <TranslationPreviewDialog
                open={translationPreviewOpen}
                onClose={() => setTranslationPreviewOpen(false)}
                campaignId={record.id}
                dataAPI={dataAPI}
            />
            {record.id != null && emailRow?.id != null && (
                <CampaignDeliveryMethodPreviewDialog
                    open={emailPreviewOpen}
                    onClose={() => setEmailPreviewOpen(false)}
                    campaignId={record.id}
                    // ``dm_id`` on the backend is the
                    // Campaign_Delivery_Method row's *primary key*, not
                    // the method type constant — passing
                    // DELIVERY_METHOD.EMAIL (=0) would look up pk=0 and
                    // 404. Use the actual saved row id, which is why we
                    // also gate render on emailRow.id being non-null
                    // (an unsaved row has id=null).
                    deliveryMethodId={emailRow.id}
                    methodLabel="Email"
                />
            )}
        </div>
    );
};

export default CampaignForm;
